import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { cachedAxiosGet } from "../utils/apiCache";
import Layout from "../components/Layout";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  BarChart,
  Area,
  Bar,
  LineChart,
  Line,
} from "recharts";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { getBackendUrl } from "../config";

const BACKEND_URL = getBackendUrl();
const CHART_CACHE_TTL_MS = 2 * 60 * 1000;

const invTodayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

function clampInvChartDate(ymd, period) {
  const t = invTodayStr();
  if (!ymd || typeof ymd !== "string") return t;
  if (period === "Day") return ymd > t ? t : ymd;
  if (period === "Month") {
    const tm = t.slice(0, 7);
    return ymd.slice(0, 7) > tm ? `${tm}-01` : ymd;
  }
  if (period === "Year") {
    const y = Number(ymd.slice(0, 4));
    const ty = new Date().getFullYear();
    return y > ty ? `${ty}-01-01` : ymd;
  }
  return ymd;
}

// Map section/label text to translation keys
const SECTION_KEYS = {
  "Status & Energy": "statusAndEnergy",
  "Power": "power",
  "PV Voltage": "pvVoltage",
  "PV Current": "pvCurrent",
  "Grid & Phase": "gridAndPhase",
  "System": "system",
};
const LABEL_KEYS = {
  "Device status": "deviceStatus",
  "Energy yield of current day": "energyYieldCurrentDay",
  "Total energy yield": "totalEnergyYield",
  "Reduced CO2 emission": "reducedCo2Emission",
  "DC power": "dcPower",
  "Active power": "activePower",
  "Reactive power": "reactivePower",
  "Power factor": "powerFactor",
  "PV1/PV2/PV3/PV4/PV5/PV6/PV7/PV8 voltage": "pv18Voltage",
  "PV9/PV10/PV11/PV12/PV13/PV14/PV15/PV16 voltage": "pv916Voltage",
  "PV17/PV18/PV19/PV20/PV21/PV22/PV23/PV24 voltage": "pv1724Voltage",
  "PV25/PV26/PV27/PV28 voltage": "pv2528Voltage",
  "PV1/PV2/PV3/PV4/PV5/PV6/PV7/PV8 current": "pv18Current",
  "PV9/PV10/PV11/PV12/PV13/PV14/PV15/PV16 current": "pv916Current",
  "PV17/PV18/PV19/PV20/PV21/PV22/PV23/PV24 current": "pv1724Current",
  "PV25/PV26/PV27/PV28 current": "pv2528Current",
  "Grid AB/BC/CA voltage": "gridAbBcCaVoltage",
  "Grid A/B/C phase current": "gridAbcPhaseCurrent",
  "Today's peak active power": "todayPeakActivePower",
  "Grid frequency": "gridFrequency",
  "Locking": "locking",
  "Cabinet temperature": "cabinetTemperature",
  "Startup time": "startupTime",
  "Shutdown time": "shutdownTime",
  "Collect DSP data": "collectDspData",
  "Active power adjustment": "activePowerAdjustment",
  "Reactive power adjustment": "reactivePowerAdjustment",
};

// Siguron që .toFixed të mos thirret kur vlera mungon ose nuk është numër
const safeToFixed = (v, decimals = 2) =>
  v != null && Number.isFinite(Number(v)) ? Number(v).toFixed(decimals) : "0.00";

// ---------- Helpers për të dhëna nga pi_inverter_readings (dizajni i njëjtë, vlera nga API) ----------
function flattenPayload(obj, prefix = "") {
  const out = {};
  if (!obj || typeof obj !== "object") return out;
  for (const [k, v] of Object.entries(obj)) {
    const key =
      (prefix ? prefix + "_" : "") +
      String(k).replace(/\s/g, "_").toLowerCase();
    if (v != null && typeof v === "object" && !Array.isArray(v)) {
      Object.assign(out, flattenPayload(v, key));
    } else {
      out[key] = v;
      out[k] = v;
    }
  }
  return out;
}

function getPayloadValue(flat, keys, fallback = "—") {
  for (const k of keys) {
    if (flat[k] != null) return String(flat[k]);
    const lower = k.toLowerCase();
    if (flat[lower] != null) return String(flat[lower]);
    if (flat[k.replace(/\s/g, "_")] != null)
      return String(flat[k.replace(/\s/g, "_")]);
  }
  return fallback;
}

// 27 parametra për Details dropdown – vlera nga Raspberry Pi (pi_inverter_readings)
const INVERTER_DETAIL_SECTIONS = [
  {
    name: "Status & Energy",
    items: [
      {
        label: "Device status",
        keys: [
          "device_status_text",
          "device_status",
          "state",
          "status",
          "run_state",
        ],
        format: "join",
      },
      {
        label: "Energy yield of current day",
        keys: ["energy_day_mwh", "energy_day_kwh", "e_today", "today_yield"],
        unit: "kWh",
        scaleMWh: true,
      },
      {
        label: "Total energy yield",
        keys: ["energy_total_mwh", "energy_total_kwh", "e_total", "total_yield"],
        unit: "MWh",
        scaleMWh: true,
      },
      {
        label: "Reduced CO2 emission",
        keys: ["reduced_co2", "co2_reduced", "co2_kg"],
        unit: "kg",
      },
    ],
  },
  {
    name: "Power",
    items: [
      {
        label: "DC power",
        keys: ["dc_power_mw", "dc_power_kw", "pv_power", "dc_power"],
        unit: "kW",
        scaleMW: true,
      },
      {
        label: "Active power",
        keys: ["active_power_mw", "active_power_kw", "pac", "ac_power"],
        unit: "kW",
        scaleMW: true,
      },
      {
        label: "Reactive power",
        keys: ["reactive_power_mvar", "reactive_power_kvar", "reactive_power", "q_total", "reactive"],
        unit: "MVar",
        scaleMVar: true,
      },
      { label: "Power factor", keys: ["power_factor", "pf"] },
    ],
  },
  {
    name: "PV Voltage",
    items: [
      {
        label: "PV1/PV2/PV3/PV4/PV5/PV6/PV7/PV8 voltage",
        keys: [
          "pv1_v",
          "pv2_v",
          "pv3_v",
          "pv4_v",
          "pv5_v",
          "pv6_v",
          "pv7_v",
          "pv8_v",
        ],
        format: "join",
        unit: "V",
      },
      {
        label: "PV9/PV10/PV11/PV12/PV13/PV14/PV15/PV16 voltage",
        keys: [
          "pv9_v",
          "pv10_v",
          "pv11_v",
          "pv12_v",
          "pv13_v",
          "pv14_v",
          "pv15_v",
          "pv16_v",
        ],
        format: "join",
        unit: "V",
      },
      {
        label: "PV17/PV18/PV19/PV20/PV21/PV22/PV23/PV24 voltage",
        keys: [
          "pv17_v",
          "pv18_v",
          "pv19_v",
          "pv20_v",
          "pv21_v",
          "pv22_v",
          "pv23_v",
          "pv24_v",
        ],
        format: "join",
        unit: "V",
      },
      {
        label: "PV25/PV26/PV27/PV28 voltage",
        keys: ["pv25_v", "pv26_v", "pv27_v", "pv28_v"],
        format: "join",
        unit: "V",
      },
    ],
  },
  {
    name: "PV Current",
    items: [
      {
        label: "PV1/PV2/PV3/PV4/PV5/PV6/PV7/PV8 current",
        keys: [
          "pv1_i",
          "pv2_i",
          "pv3_i",
          "pv4_i",
          "pv5_i",
          "pv6_i",
          "pv7_i",
          "pv8_i",
        ],
        format: "join",
        unit: "A",
      },
      {
        label: "PV9/PV10/PV11/PV12/PV13/PV14/PV15/PV16 current",
        keys: [
          "pv9_i",
          "pv10_i",
          "pv11_i",
          "pv12_i",
          "pv13_i",
          "pv14_i",
          "pv15_i",
          "pv16_i",
        ],
        format: "join",
        unit: "A",
      },
      {
        label: "PV17/PV18/PV19/PV20/PV21/PV22/PV23/PV24 current",
        keys: [
          "pv17_i",
          "pv18_i",
          "pv19_i",
          "pv20_i",
          "pv21_i",
          "pv22_i",
          "pv23_i",
          "pv24_i",
        ],
        format: "join",
        unit: "A",
      },
      {
        label: "PV25/PV26/PV27/PV28 current",
        keys: ["pv25_i", "pv26_i", "pv27_i", "pv28_i"],
        format: "join",
        unit: "A",
      },
    ],
  },
  {
    name: "Grid & Phase",
    items: [
      {
        label: "Grid AB/BC/CA voltage",
        keys: [
          "grid_ab_voltage",
          "grid_bc_voltage",
          "grid_ca_voltage",
          "grid_ab_bc_ca",
          "grid_voltage",
          "voltage_ab_bc_ca",
        ],
        format: "join",
        unit: "V",
      },
      {
        label: "Grid A/B/C phase current",
        keys: [
          "grid_a_current",
          "grid_b_current",
          "grid_c_current",
          "grid_phase_current",
          "phase_current_abc",
          "current_a_b_c",
        ],
        format: "join",
        unit: "A",
      },
      {
        label: "Today's peak active power",
        keys: [
          "today_peak_mw",
          "peak_active_power_mw",
          "today_peak_kw",
          "peak_active_power_kw",
          "today_peak_power",
          "peak_power",
        ],
        unit: "kW",
        scaleMW: true,
      },
      {
        label: "Grid frequency",
        keys: ["grid_frequency", "frequency", "freq", "hz"],
        unit: "Hz",
      },
    ],
  },
  {
    name: "System",
    items: [
      { label: "Locking", keys: ["locking", "lock_status", "locked"] },
      {
        label: "Cabinet temperature",
        keys: ["cabinet_temp", "temperature", "cabinet_temperature"],
        unit: "degC",
      },
      {
        label: "Startup time",
        keys: ["startup_time_text", "startup_time", "start_time"],
      },
      {
        label: "Shutdown time",
        keys: ["shutdown_time_text", "shutdown_time", "stop_time"],
      },
      {
        label: "Collect DSP data",
        keys: [
          "collect_dsp_data_text",
          "collect_dsp_data",
          "dsp_data",
          "dsp_status",
        ],
      },
      {
        label: "Active power adjustment",
        keys: ["active_power_adjustment", "p_adjustment", "active_adjustment"],
      },
      {
        label: "Reactive power adjustment",
        keys: [
          "reactive_power_adjustment",
          "pf_adjustment",
          "reactive_adjustment",
        ],
      },
    ],
  },
];

function buildInverterDetailSections(payload) {
  const flat = flattenPayload(payload || {});
  const get = (keys) => {
    for (const k of keys) {
      const v = flat[k];
      if (v != null && v !== "") return v;
      const lower = String(k).toLowerCase();
      if (flat[lower] != null) return flat[lower];
    }
    return null;
  };
  const getWithKey = (keyList) => {
    for (const k of keyList) {
      const v = flat[k] ?? flat[String(k).toLowerCase()];
      if (v != null && v !== "") return { val: v, key: String(k).toLowerCase() };
    }
    return null;
  };
  // Mos shfaq vlera hardcoded kur payload-i i fundit mungon/null.
  // Në këtë rast, UI duhet të tregojë "—".
  const defaults = {};
  return INVERTER_DETAIL_SECTIONS.map((section) => ({
    name: section.name,
    items: section.items.map((item) => {
      let val;

      if (item.format === "join") {
        const values = item.keys
          .map((k) => flat[k])
          .filter((v) => v !== null && v !== undefined && v !== "");

        val = values.length ? values.join(", ") : null;
        if (item.label === "Device status" && val && (String(val).toLowerCase().includes("no irradiation") || String(val).includes("40960"))) {
          val = "Standby";
        }
      } else if (item.scaleMWh || item.scaleMW || item.scaleMVar) {
        const r = getWithKey(item.keys);
        if (r) {
          const n = Number(r.val);
          const k = r.key;
          const alreadyM = /_(mwh|mw|mvar)$/.test(k);
          val = !Number.isNaN(n) ? (alreadyM ? n : n / 1000) : r.val;
        }
      } else {
        val = get(item.keys);
      }

      if (val == null || val === "") val = defaults[item.label] ?? "—";

      if (Array.isArray(val)) val = val.join(", ");
      else if (typeof val === "number" && (item.scaleMWh || item.scaleMW || item.scaleMVar)) {
        if (item.unit === "kWh" && item.scaleMWh) val = val * 1000;
        else if (item.unit === "MWh" && item.scaleMWh) { /* keep val in MWh */ }
        else if (item.unit === "kW" && item.scaleMW) val = val * 1000;
        val = val < 0.01 && val >= 0 ? safeToFixed(val, 4) : safeToFixed(val, 2);
      } else val = String(val);

      if (
        item.unit &&
        val !== "—" &&
        !/\s*(MWh|MW|MVar|kWh|kW|kVar|V|A|Hz|kg|degC)\s*$/.test(val)
      )
        val += (val ? " " : "") + item.unit;

      return { label: item.label, value: val };
    }),
  }));
}

const FIXED_INVERTER_PARAM_SECTIONS = [
  {
    id: "pv",
    title: "PV Information",
    items: [
      { label: "PV1 Voltage", keys: ["pv1_voltage", "pv1_v", "pv_voltage_1"] },
      { label: "PV1 Current", keys: ["pv1_current", "pv1_i", "pv_current_1"] },
      { label: "PV2 Voltage", keys: ["pv2_voltage", "pv2_v", "pv_voltage_2"] },
      { label: "PV2 Current", keys: ["pv2_current", "pv2_i", "pv_current_2"] },
    ],
  },
  {
    id: "ac",
    title: "AC Information",
    items: [
      { label: "AC Voltage", keys: ["ac_voltage", "ac_v", "voltage_ac"] },
      { label: "AC Current", keys: ["ac_current", "ac_i", "current_ac"] },
      {
        label: "AC Frequency",
        keys: ["ac_frequency", "ac_freq", "frequency", "freq", "hz"],
      },
    ],
  },
  {
    id: "grid",
    title: "Grid Information",
    items: [
      { label: "Grid Power", keys: ["grid_power", "grid_p", "power_grid"] },
      { label: "Grid Status", keys: ["grid_status", "grid_state", "status"] },
    ],
  },
  {
    id: "load",
    title: "Load Information",
    items: [
      { label: "Load Power", keys: ["load_power", "load_p", "power_load"] },
      {
        label: "Load Current",
        keys: ["load_current", "load_i", "current_load"],
      },
    ],
  },
];

function buildInverterParamSectionsFromPayload(payload) {
  const flat = flattenPayload(payload || {});
  return FIXED_INVERTER_PARAM_SECTIONS.map((section) => ({
    id: section.id,
    title: section.title,
    data: section.items.map((item) => ({
      label: item.label,
      value: getPayloadValue(flat, item.keys),
    })),
  }));
}

const INVERTER_STAT_KEYS = {
  pv_power: [
    "active_power_kw",
    "dc_power_kw",
    "pv_power",
    "power",
    "pac",
    "ac_power",
    "output_power",
  ],
  today_yield: [
    "energy_day_kwh",
    "today_yield",
    "e_today",
    "energy_today",
    "daily_yield",
  ],
  monthly_yield: [
    "monthly_yield",
    "e_month",
    "energy_month",
    "monthly_yield_kwh",
  ],
  total_yield: [
    "energy_total_kwh",
    "total_yield",
    "e_total",
    "energy_total",
    "total_yield_kwh",
  ],
  state: [
    "device_status_text",
    "device_status",
    "state",
    "status",
    "run_state",
    "work_state",
  ],
};

function buildInverterMockDataFromPayload(payload, inverter) {
  const flat = flattenPayload(payload || {});
  const get = (keyList) => {
    const v = getPayloadValue(flat, keyList, null);
    return v === null || v === "—" ? undefined : v;
  };
  const toNum = (v) => (v == null || v === "" || Number.isNaN(Number(v))) ? 0 : Number(v);
  const rawState = get(INVERTER_STAT_KEYS.state) ?? "Normal";
  const stateShort = typeof rawState === "string" && rawState.includes(":")
    ? rawState.split(":")[0].trim()
    : rawState;
  const pwKw = toNum(get(INVERTER_STAT_KEYS.pv_power));
  const pwMwRaw = (flat.active_power_mw != null || flat.dc_power_mw != null)
    ? toNum(flat.active_power_mw ?? flat.dc_power_mw)
    : pwKw / 1000;
  const pwMw = Math.abs(pwMwRaw);
  const dayKwh = toNum(get(INVERTER_STAT_KEYS.today_yield));
  const monthKwh = toNum(get(INVERTER_STAT_KEYS.monthly_yield));
  const totalKwh = toNum(get(INVERTER_STAT_KEYS.total_yield));
  const dayMWh = (flat.energy_day_mwh != null) ? toNum(flat.energy_day_mwh) : dayKwh / 1000;
  const monthMWh = (flat.energy_month_mwh != null) ? toNum(flat.energy_month_mwh) : monthKwh / 1000;
  const totalMWh = (flat.energy_total_mwh != null) ? toNum(flat.energy_total_mwh) : totalKwh / 1000;
  return {
    pv_power: (pwMw < 0.001 && pwMw >= 0) ? safeToFixed(pwMw, 4) : safeToFixed(pwMw, 3),
    today_yield: (dayMWh < 0.01 && dayMWh >= 0) ? safeToFixed(dayMWh, 3) : safeToFixed(dayMWh, 2),
    monthly_yield: (monthMWh < 0.01 && monthMWh >= 0) ? safeToFixed(monthMWh, 3) : safeToFixed(monthMWh, 2),
    total_yield: (totalMWh < 0.01 && totalMWh >= 0) ? safeToFixed(totalMWh, 3) : safeToFixed(totalMWh, 2),
    state: stateShort,
    model: inverter?.model || "—",
  };
}

function formatTimeAgo(ts) {
  if (!ts) return null;
  const d = new Date(ts);
  if (isNaN(d.getTime())) return null;
  const sec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)} min ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)} h ago`;
  return `${Math.floor(sec / 86400)} days ago`;
}

// Format alarm for display: "License Expired: OFF" – name readable (snake_case → Title Case), state ON/OFF
function formatAlarmDisplay(a) {
  const raw = (a?.name ?? a?.message ?? a?.description ?? "").trim();
  const name = raw
    ? raw.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
    : "Alarm";
  const state = (a?.state ?? "OFF").toUpperCase();
  return `${name}: ${state}`;
}

// Nxjerr fuqinë (kW) nga payload – |kW| për PV / aktive në grafik
function getPowerFromPayload(payload) {
  if (!payload || typeof payload !== "object") return 0;
  const flat = flattenPayload(payload);
  const keys = [
    "active_power_kw",
    "dc_power_kw",
    "peak_active_power_kw", // Pi inverter.py
    "pv_power",
    "power",
    "pac",
    "ac_power",
    "output_power",
    "P",
    "p",
  ];
  for (const k of keys) {
    const v = flat[k];
    if (v != null && v !== "") {
      const n = Number(v);
      if (!Number.isNaN(n)) return Math.abs(n);
    }
  }
  const mwKeys = ["active_power_mw", "dc_power_mw", "input_power_mw"];
  for (const k of mwKeys) {
    const v = flat[k];
    if (v != null && v !== "") {
      const n = Number(v);
      if (!Number.isNaN(n)) return Math.abs(n * 1000);
    }
  }
  return 0;
}

// Kthen true vetëm kur payload ka të paktën një telemetri reale (jo null/---).
function hasInverterTelemetry(payload) {
  if (!payload || typeof payload !== "object") return false;
  const flat = flattenPayload(payload);
  const keys = [
    "active_power_kw",
    "dc_power_kw",
    "energy_day_kwh",
    "energy_total_kwh",
    "grid_ab_voltage",
    "grid_frequency",
    "device_status",
    "device_status_code",
    "startup_time",
  ];
  return keys.some((k) => {
    const v = flat[k];
    if (v == null) return false;
    if (typeof v === "string") {
      const s = v.trim().toLowerCase();
      return s !== "" && s !== "null" && s !== "undefined" && s !== "---" && s !== "—";
    }
    return true;
  });
}

// Nxjerr energji totale kumulative (kWh) nga payload – për llogaritjen e monthly = last - first
function getTotalEnergyKwhFromPayload(payload) {
  if (!payload || typeof payload !== "object") return null;
  const flat = flattenPayload(payload);
  const keys = ["energy_total_kwh", "total_yield", "e_total", "energy_total", "total_yield_kwh"];
  for (const k of keys) {
    const v = flat[k];
    if (v != null && v !== "") {
      const n = Number(v);
      if (!Number.isNaN(n)) return n;
    }
  }
  return null;
}

// Nxjerr energji të ditës (kWh) nga payload ose nga reading – energy_day_kwh, today_yield, e_today, etj.
function getDayEnergyKwhFromPayload(payloadOrReading) {
  if (!payloadOrReading || typeof payloadOrReading !== "object") return null;
  const keys = ["energy_day_kwh", "today_yield", "e_today", "energy_today", "daily_yield"];
  const tryObj = (obj) => {
    if (!obj || typeof obj !== "object") return null;
    for (const k of keys) {
      const v = obj[k];
      if (v != null && v !== "") {
        const n = Number(v);
        if (!Number.isNaN(n)) return n;
      }
    }
    const flat = flattenPayload(obj);
    for (const k of keys) {
      const v = flat[k];
      if (v != null && v !== "") {
        const n = Number(v);
        if (!Number.isNaN(n)) return n;
      }
    }
    if (obj.energy_day_mwh != null && obj.energy_day_mwh !== "") {
      const n = Number(obj.energy_day_mwh);
      if (!Number.isNaN(n)) return n * 1000;
    }
    return null;
  };
  // Provë: reading i plotë (top-level), pastaj payload, pastaj display_payload
  const v = tryObj(payloadOrReading) ?? tryObj(payloadOrReading?.payload) ?? tryObj(payloadOrReading?.display_payload);
  return v;
}

// Nxjerr energji/yield (kWh) nga payload – match me Pi (energy_day_kwh, energy_total_kwh) dhe varianta të tjera
function getEnergyFromPayload(payload) {
  if (!payload || typeof payload !== "object") return 0;
  const flat = flattenPayload(payload);
  const keys = [
    "energy_day_kwh",
    "energy_total_kwh", // Pi inverter.py
    "today_yield",
    "e_today",
    "energy_today",
    "daily_yield",
    "total_yield",
    "e_total",
    "energy_total",
    "monthly_yield",
    "e_month",
  ];
  for (const k of keys) {
    const v = flat[k];
    if (v != null && v !== "") {
      const n = Number(v);
      if (!Number.isNaN(n)) return n;
    }
  }
  return getPowerFromPayload(payload);
}

// Formato kohën pa "paradite/pasdites" – dinamike 06:00-18:00
function formatTimeHHmm(date) {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) return "—";
  const h = date.getHours();
  const m = date.getMinutes();
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

// Kontrollon në koha është brenda prodhimit 06:00–20:00 (përfshirë vetëm deri 20:00:00, jo 20:01)
function isWithinProductionHours(rt) {
  if (!rt || !(rt instanceof Date) || isNaN(rt.getTime())) return false;
  const h = rt.getHours();
  const m = rt.getMinutes();
  if (h < 6) return false;
  if (h > 20) return false;
  if (h === 20 && m > 0) return false; // 20:01, 20:57 etj jashtë
  return true;
}

// Ndërton të dhëna grafiku Real-time nga readings (kohë + fuqi)
function buildRealTimeChartData(readings, selectedDate = null, period = "Day") {
  if (!readings || !readings.length) return [{ time: "—", value: 0 }];
  let filtered = readings;
  if (selectedDate && period === "Day") {
    const d = new Date(selectedDate);
    const dayStart = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const dayEnd = new Date(dayStart);
    dayEnd.setDate(dayEnd.getDate() + 1);
    filtered = readings.filter((r) => {
      const rt = r.ts ? new Date(r.ts) : null;
      return !!rt && rt >= dayStart && rt < dayEnd;
    });
  } else if (selectedDate && period === "Month") {
    const d = new Date(selectedDate);
    const monthStart = new Date(d.getFullYear(), d.getMonth(), 1);
    const monthEnd = new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59);
    filtered = readings.filter((r) => {
      const rt = r.ts ? new Date(r.ts) : null;
      if (!rt || rt < monthStart || rt > monthEnd) return false;
      return isWithinProductionHours(rt);
    });
  } else if (selectedDate && period === "Year") {
    const d = new Date(selectedDate);
    const yearStart = new Date(d.getFullYear(), 0, 1);
    const yearEnd = new Date(d.getFullYear(), 11, 31, 23, 59, 59);
    filtered = readings.filter((r) => {
      const rt = r.ts ? new Date(r.ts) : null;
      if (!rt || rt < yearStart || rt > yearEnd) return false;
      return isWithinProductionHours(rt);
    });
  }
  // Për Month/Year/Lifetime pa datë: filtrim vetëm 06:00–20:00 (jo 20:01+)
  if (period !== "Day") {
    filtered = filtered.filter((r) => {
      const rt = r.ts ? new Date(r.ts) : null;
      return isWithinProductionHours(rt);
    });
  }
  // Day: 00:00–23:59 (bucket 5 min: 00:00, 00:05, 00:10 …)
  if (period === "Day") {
    const d = selectedDate ? new Date(selectedDate) : new Date();
    const now = new Date();
    const isToday =
      d.getFullYear() === now.getFullYear() &&
      d.getMonth() === now.getMonth() &&
      d.getDate() === now.getDate();
    const endOfDay = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59);
    const stopAt = isToday ? now : endOfDay;
    const bySlot = {};
    filtered.forEach((r) => {
      const rt = r.ts ? new Date(r.ts) : null;
      if (!rt) return;
      const m5 = Math.floor(rt.getMinutes() / 5) * 5;
      const key = `${String(rt.getHours()).padStart(2, "0")}:${String(m5).padStart(2, "0")}`;
      if (!bySlot[key]) bySlot[key] = { sum: 0, count: 0 };
      bySlot[key].sum += getPowerFromPayload(r.payload);
      bySlot[key].count += 1;
    });
    const result = [];
    for (let h = 0; h <= 23; h++) {
      for (let m = 0; m < 60; m += 5) {
        const slotStart = new Date(d.getFullYear(), d.getMonth(), d.getDate(), h, m, 0);
        if (slotStart > stopAt) return result.length ? result : [{ time: "00:00", name: "00:00", value: 0 }];
        const timeStr = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
        const slot = bySlot[timeStr];
        const avg = slot && slot.count > 0 ? slot.sum / slot.count : 0;
        result.push({ time: timeStr, name: timeStr, value: avg });
      }
    }
    return result.length ? result : [{ time: "00:00", name: "00:00", value: 0 }];
  }

  // Month: ditë 01 deri sot kur është muaji aktual, përndryshe 01–31
  if (period === "Month") {
    const d = selectedDate ? new Date(selectedDate) : new Date();
    const now = new Date();
    const daysInMonth = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
    const isCurrentMonth = d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
    const endDay = isCurrentMonth ? now.getDate() : daysInMonth;
    const byDay = {};
    for (let day = 1; day <= daysInMonth; day++) byDay[day] = { sum: 0, count: 0 };
    filtered.forEach((r) => {
      const rt = r.ts ? new Date(r.ts) : null;
      if (!rt) return;
      if (isWithinProductionHours(rt)) {
        const day = rt.getDate();
        byDay[day].sum += getPowerFromPayload(r.payload);
        byDay[day].count += 1;
      }
    });
    const result = [];
    const yr = d.getFullYear();
    const mo = d.getMonth() + 1;
    for (let day = 1; day <= endDay; day++) {
      const avg = byDay[day].count > 0 ? byDay[day].sum / byDay[day].count : 0;
      const nameStr = `${String(day).padStart(2, "0")}.${String(mo).padStart(2, "0")}.${String(yr).slice(-2)}`;
      result.push({ time: nameStr, name: nameStr, value: avg });
    }
    if (result.length === 0) return [{ time: "01", name: "01", value: 0 }];
    return result;
  }

  // Year: Jan deri në muajin aktual kur është viti aktual, përndryshe 12 muaj
  if (period === "Year") {
    const d = selectedDate ? new Date(selectedDate) : new Date();
    const now = new Date();
    const isCurrentYear = d.getFullYear() === now.getFullYear();
    const endMonth = isCurrentYear ? now.getMonth() : 11;
    const byMonth = {};
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "Maj", "Qer", "Kor", "Gus", "Sht", "Tet", "Nën", "Dhj"];
    for (let m = 0; m < 12; m++) byMonth[m] = { sum: 0, count: 0 };
    filtered.forEach((r) => {
      const rt = r.ts ? new Date(r.ts) : null;
      if (!rt) return;
      if (isWithinProductionHours(rt)) {
        const m = rt.getMonth();
        byMonth[m].sum += getPowerFromPayload(r.payload);
        byMonth[m].count += 1;
      }
    });
    const result = [];
    const yr = d.getFullYear();
    for (let m = 0; m <= endMonth; m++) {
      const avg = byMonth[m].count > 0 ? byMonth[m].sum / byMonth[m].count : 0;
      const nameStr = `${monthNames[m]} ${yr}`;
      result.push({ time: nameStr, name: nameStr, value: avg });
    }
    return result;
  }

  // Lifetime: 12 muajt e fundit (ndarë me muaj)
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "Maj", "Qer", "Kor", "Gus", "Sht", "Tet", "Nën", "Dhj"];
  const byKey = {}; // "2024-08" -> { sum, count }
  filtered.forEach((r) => {
    const rt = r.ts ? new Date(r.ts) : null;
    if (!rt) return;
    if (isWithinProductionHours(rt)) {
      const key = `${rt.getFullYear()}-${String(rt.getMonth() + 1).padStart(2, "0")}`;
      if (!byKey[key]) byKey[key] = { sum: 0, count: 0 };
      byKey[key].sum += getPowerFromPayload(r.payload);
      byKey[key].count += 1;
    }
  });
  const now = new Date();
  const result = [];
  for (let i = 11; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const bucket = byKey[key] || { sum: 0, count: 0 };
    const avg = bucket.count > 0 ? bucket.sum / bucket.count : 0;
    const nameStr = `${monthNames[d.getMonth()]} ${d.getFullYear()}`;
    result.push({ time: nameStr, name: nameStr, value: avg });
  }
  return result;
}

// Ndërton të dhëna grafiku Analysis nga readings (period = day/week/month/year/total)
// Day: orë 06–20. Week: 7 ditë. Month: ditë 01–31. Year: 12 muaj. Total: 12 muaj.
function buildAnalysisChartData(readings, period, selectedDate = null) {
  if (!readings || !readings.length) return [{ name: "—", value: 0 }];

  let filtered = readings;
  const refDate = selectedDate ? new Date(selectedDate) : new Date();

  if (period === "week") {
    const weekEnd = new Date(refDate.getFullYear(), refDate.getMonth(), refDate.getDate(), 23, 59, 59);
    const weekStart = new Date(weekEnd);
    weekStart.setDate(weekStart.getDate() - 6);
    weekStart.setHours(0, 0, 0, 0);
    filtered = readings.filter((r) => {
      const rt = r.ts ? new Date(r.ts) : null;
      return rt && rt >= weekStart && rt <= weekEnd && isWithinProductionHours(rt);
    });
  } else if (period === "month") {
    const monthStart = new Date(refDate.getFullYear(), refDate.getMonth(), 1);
    const monthEnd = new Date(refDate.getFullYear(), refDate.getMonth() + 1, 0, 23, 59, 59);
    filtered = readings.filter((r) => {
      const rt = r.ts ? new Date(r.ts) : null;
      return rt && rt >= monthStart && rt <= monthEnd && isWithinProductionHours(rt);
    });
  } else if (period === "year") {
    const yearStart = new Date(refDate.getFullYear(), 0, 1);
    const yearEnd = new Date(refDate.getFullYear(), 11, 31, 23, 59, 59);
    filtered = readings.filter((r) => {
      const rt = r.ts ? new Date(r.ts) : null;
      return rt && rt >= yearStart && rt <= yearEnd && isWithinProductionHours(rt);
    });
  } else if (period === "total") {
    filtered = readings.filter((r) => {
      const rt = r.ts ? new Date(r.ts) : null;
      return isWithinProductionHours(rt);
    });
  } else {
    const dayStart = new Date(refDate.getFullYear(), refDate.getMonth(), refDate.getDate());
    const dayEnd = new Date(dayStart);
    dayEnd.setDate(dayEnd.getDate() + 1);
    filtered = readings.filter((r) => {
      const rt = r.ts ? new Date(r.ts) : null;
      return rt && rt >= dayStart && rt < dayEnd && isWithinProductionHours(rt);
    });
  }

  const getVal = (r) => getEnergyFromPayload(r.payload) || getPowerFromPayload(r.payload);

  // day: çdo orë 06:00–20:00
  if (period === "day") {
    const byHour = {};
    for (let h = 6; h <= 20; h++) byHour[h] = { sum: 0, count: 0 };
    filtered.forEach((r) => {
      const rt = r.ts ? new Date(r.ts) : null;
      if (!rt || !isWithinProductionHours(rt)) return;
      const h = rt.getHours();
      byHour[h].sum += getVal(r);
      byHour[h].count += 1;
    });
    const result = [];
    for (let h = 6; h <= 20; h++) {
      const avg = byHour[h].count > 0 ? byHour[h].sum / byHour[h].count : 0;
      result.push({ name: `${String(h).padStart(2, "0")}:00`, value: avg });
    }
    return result;
  }

  // week: 7 ditë
  if (period === "week") {
    const byDay = {};
    for (let i = 0; i < 7; i++) {
      const d = new Date(refDate);
      d.setDate(d.getDate() - 6 + i);
      byDay[d.toDateString()] = { sum: 0, count: 0 };
    }
    filtered.forEach((r) => {
      const rt = r.ts ? new Date(r.ts) : null;
      if (!rt) return;
      const key = rt.toDateString();
      if (byDay[key]) {
        byDay[key].sum += getVal(r);
        byDay[key].count += 1;
      }
    });
    const result = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(refDate);
      d.setDate(d.getDate() - 6 + i);
      const key = d.toDateString();
      const b = byDay[key] || { sum: 0, count: 0 };
      const avg = b.count > 0 ? b.sum / b.count : 0;
      const nameStr = `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}`;
      result.push({ name: nameStr, value: avg });
    }
    return result;
  }

  // month: 01–31 (çdo ditë)
  if (period === "month") {
    const daysInMonth = new Date(refDate.getFullYear(), refDate.getMonth() + 1, 0).getDate();
    const byDay = {};
    for (let day = 1; day <= daysInMonth; day++) byDay[day] = { sum: 0, count: 0 };
    filtered.forEach((r) => {
      const rt = r.ts ? new Date(r.ts) : null;
      if (!rt) return;
      const day = rt.getDate();
      byDay[day].sum += getVal(r);
      byDay[day].count += 1;
    });
    const result = [];
    const yr = refDate.getFullYear();
    const mo = refDate.getMonth() + 1;
    for (let day = 1; day <= daysInMonth; day++) {
      const avg = byDay[day].count > 0 ? byDay[day].sum / byDay[day].count : 0;
      const nameStr = `${String(day).padStart(2, "0")}.${String(mo).padStart(2, "0")}.${String(yr).slice(-2)}`;
      result.push({ name: nameStr, value: avg });
    }
    return result.length ? result : [{ name: "01", value: 0 }];
  }

  // year dhe total: 12 muaj
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "Maj", "Qer", "Kor", "Gus", "Sht", "Tet", "Nën", "Dhj"];
  if (period === "year") {
    const byMonth = {};
    for (let m = 0; m < 12; m++) byMonth[m] = { sum: 0, count: 0 };
    filtered.forEach((r) => {
      const rt = r.ts ? new Date(r.ts) : null;
      if (!rt) return;
      byMonth[rt.getMonth()].sum += getVal(r);
      byMonth[rt.getMonth()].count += 1;
    });
    const result = [];
    for (let m = 0; m < 12; m++) {
      const avg = byMonth[m].count > 0 ? byMonth[m].sum / byMonth[m].count : 0;
      result.push({ name: `${monthNames[m]} ${refDate.getFullYear()}`, value: avg });
    }
    return result;
  }

  // total: 12 muajt e fundit
  const byKey = {};
  filtered.forEach((r) => {
    const rt = r.ts ? new Date(r.ts) : null;
    if (!rt) return;
    const key = `${rt.getFullYear()}-${String(rt.getMonth() + 1).padStart(2, "0")}`;
    if (!byKey[key]) byKey[key] = { sum: 0, count: 0 };
    byKey[key].sum += getVal(r);
    byKey[key].count += 1;
  });
  const result = [];
  const now = new Date();
  for (let i = 11; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const b = byKey[key] || { sum: 0, count: 0 };
    const avg = b.count > 0 ? b.sum / b.count : 0;
    result.push({ name: `${monthNames[d.getMonth()]} ${d.getFullYear()}`, value: avg });
  }
  return result;
}

// ============================================
// MOBILE VIEW COMPONENT
// ============================================
const MobileInverterView = ({
  inverter,
  dataLogger,
  currentCompany,
  navigate,
  mockData,
  isDark,
  allInverters,
  companies,
  inverterDetailSections = [],
  lastUpdated,
  alarms = [],
  alarmHistory = [],
  chartPeriod,
  setChartPeriod,
  selectedChartDate,
  setSelectedChartDate,
  onChartDateNavigate,
  readingsForChart = [],
  chartLoading = false,
  t = (k) => k,
}) => {
  const mobileRealtimeDatePickerRef = useRef(null);
  const mobileRealtimeMonthPickerRef = useRef(null);
  const [mainTab, setMainTab] = useState("details"); // details | realtime | alarms
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [expandedParam, setExpandedParam] = useState(null);
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);

  // Siguro numra për mobile (API mund të kthejë string) – shmang .toFixed is not a function
  const pvPowerNum = Number(mockData?.pv_power) || 0;  // në MW nga payload
  const pvPowerKw = pvPowerNum * 1000;                 // konverto në kW për krahasim me kapacitet
  const capacityKw = Number(inverter?.power_kw) || Number(inverter?.rated_power_kw) || 0;
  const pvPercent = capacityKw > 0 ? Math.min(100, (pvPowerKw / capacityKw) * 100) : 0;
  const todayYieldNum = Number(mockData?.today_yield) || 0;
  const totalYieldNum = Number(mockData?.total_yield) || 0;

  const currentInverterIndex =
    allInverters?.findIndex((inv) => inv.id === inverter.id) ?? 0;

  const goToPrevInverter = () => {
    if (allInverters && allInverters.length > 1) {
      const prevIndex =
        (currentInverterIndex - 1 + allInverters.length) % allInverters.length;
      const prevInverter = allInverters[prevIndex];
      navigate(
        `/devices/${prevInverter.data_logger_id}/inverter/${prevInverter.id}`,
      );
    }
  };

  const goToNextInverter = () => {
    if (allInverters && allInverters.length > 1) {
      const nextIndex = (currentInverterIndex + 1) % allInverters.length;
      const nextInverter = allInverters[nextIndex];
      navigate(
        `/devices/${nextInverter.data_logger_id}/inverter/${nextInverter.id}`,
      );
    }
  };

  // Të njëjtat të dhëna si web: fetch me date_from/date_to nga prindi (jo vetëm limit=300).
  const graphData = buildRealTimeChartData(readingsForChart, selectedChartDate, chartPeriod);
  const chartDataKey = chartPeriod === "Day" ? "time" : "name";

  const openMobileInvRealtimePicker = () => {
    if (chartPeriod === "Day") {
      mobileRealtimeDatePickerRef.current?.showPicker?.();
      mobileRealtimeDatePickerRef.current?.click();
    } else if (chartPeriod === "Month") {
      mobileRealtimeMonthPickerRef.current?.showPicker?.();
      mobileRealtimeMonthPickerRef.current?.click();
    }
  };

  const today = new Date();
  const dateStr =
    selectedChartDate ||
    `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  const invMobileRealtimeGraphDateDisplay =
    chartPeriod === "Day"
      ? dateStr
      : chartPeriod === "Month"
        ? (selectedChartDate ? selectedChartDate.slice(0, 7) : "")
        : chartPeriod === "Year"
          ? (selectedChartDate ? selectedChartDate.slice(0, 4) : "")
          : "";
  const timeStr =
    lastUpdated ||
    `${dateStr} ${String(today.getHours()).padStart(2, "0")}:${String(today.getMinutes()).padStart(2, "0")}:${String(today.getSeconds()).padStart(2, "0")}`;

  // inverterDetailSections nga props (27 parametra, i njëjtë si web – pi_inverter_readings)

  // Card shadow class - same as Devices page
  const cardShadowClass =
    "shadow-[6px_6px_12px_rgba(0,0,0,0.08),-6px_-6px_12px_rgba(255,255,255,0.9)] dark:shadow-none";

  return (
    <Layout>
      <div className="h-full flex flex-col bg-[#f5f5f5] dark:bg-gradient-to-b dark:from-[#0f172a] dark:to-black">
        {/* Header with Company Dropdown - Same as Devices page */}
        <div className="p-4 border-b border-gray-200 dark:border-slate-700 bg-white dark:bg-[#0f172a]">
          <div className="flex items-center justify-between mb-3">
            <div className="relative">
              <button
                onClick={() => setShowCompanyDropdown(!showCompanyDropdown)}
                className="flex items-center gap-2"
              >
                <span className="text-lg font-bold text-gray-900 dark:text-white">
                  {currentCompany?.name || "FRIGOFOOD"}
                </span>
                <svg
                  className={`w-4 h-4 text-gray-500 dark:text-gray-400 transition-transform ${showCompanyDropdown ? "rotate-180" : ""}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {/* Company Dropdown Menu */}
              {showCompanyDropdown && companies && companies.length > 0 && (
                <div className="absolute top-full left-0 mt-2 w-56 bg-white dark:bg-[#1e293b] rounded-xl shadow-lg z-50 overflow-hidden border border-gray-200 dark:border-transparent">
                  {companies.map((company) => (
                    <button
                      key={company.id}
                      onClick={() => {
                        setShowCompanyDropdown(false);
                        navigate(`/devices?company=${company.id}`);
                      }}
                      className={`w-full text-left px-4 py-3 hover:bg-gray-100 dark:hover:bg-[#2d3a4f] transition border-b border-gray-100 dark:border-[#2d3a4f] last:border-0 ${currentCompany?.id === company.id
                        ? "bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 font-medium"
                        : "text-gray-700 dark:text-white"
                        }`}
                    >
                      {company.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {/* X Close button */}
            <button
              onClick={() =>
                currentCompany
                  ? navigate(`/devices?company=${currentCompany.id}`)
                  : navigate("/devices")
              }
              className="w-10 h-10 bg-[#e8e8e8] dark:bg-slate-700 hover:bg-gray-300 dark:hover:bg-slate-600 rounded-full flex items-center justify-center text-gray-600 dark:text-white transition"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Tabs – dimension njëjtë si Pajisjet: px-2.5 py-1.5 text-xs shrink-0, vetëm mobile */}
          <div className="flex gap-1.5 flex-1 min-w-0">
            <button
              onClick={() =>
                currentCompany
                  ? navigate(`/devices?company=${currentCompany.id}`)
                  : navigate("/devices")
              }
              className="px-3 py-2 rounded-full text-sm font-medium transition shrink-0 bg-[#e8e8e8] dark:bg-slate-700 text-gray-600 dark:text-gray-300"
            >
              {t("allDevices")}
            </button>
            <button
              onClick={() =>
                currentCompany
                  ? navigate(
                    `/devices?company=${currentCompany.id}&tab=inverter`,
                  )
                  : navigate("/devices?tab=inverter")
              }
              className="px-3 py-2 rounded-full text-sm font-medium transition shrink-0 bg-blue-600 text-white"
            >
              {t("inverterTab")}
            </button>
            <button
              onClick={() =>
                currentCompany
                  ? navigate(`/devices?company=${currentCompany.id}&tab=ems`)
                  : navigate("/devices?tab=ems")
              }
              className="px-3 py-2 rounded-full text-sm font-medium transition shrink-0 bg-[#e8e8e8] dark:bg-slate-700 text-gray-600 dark:text-gray-300"
            >
              {t("emsTab")}
            </button>
          </div>
        </div>

        {/* Section Title */}
        <div className="px-4 py-3 bg-[#f0f0f0] dark:bg-[#0f172a]/80">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">
            {t("inverterDetails")}
          </h3>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-4 pb-4">
          {/* Status Badge */}
          <div className="py-3">
            <span className="inline-block px-3 py-1 bg-[#22c55e] text-white text-xs font-bold rounded">
              {mockData?.state || "Normal"}
            </span>
          </div>

          {/* Image Carousel - Same style as Devices page */}
          <div
            className={`relative bg-white dark:bg-[#1e293b] rounded-xl p-6 mb-4 ${cardShadowClass}`}
          >
            <div className="h-32 flex items-center justify-center">
              {inverter.photo_url ? (
                <img
                  src={`${BACKEND_URL}${inverter.photo_url}`}
                  alt={inverter.name}
                  className="max-h-full max-w-full object-contain"
                />
              ) : (
                <svg
                  className="w-20 h-20 text-gray-300 dark:text-slate-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z"
                  />
                </svg>
              )}
            </div>
            {/* Navigation arrows */}
            {allInverters && allInverters.length > 1 && (
              <>
                <button
                  onClick={goToPrevInverter}
                  className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-600 dark:text-white text-2xl bg-white/80 dark:bg-black/50 rounded-full w-8 h-8 flex items-center justify-center hover:bg-gray-200 dark:hover:bg-black/70 transition"
                >
                  ‹
                </button>
                <button
                  onClick={goToNextInverter}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-600 dark:text-white text-2xl bg-white/80 dark:bg-black/50 rounded-full w-8 h-8 flex items-center justify-center hover:bg-gray-200 dark:hover:bg-black/70 transition"
                >
                  ›
                </button>
                <div className="absolute bottom-2 right-4 text-gray-500 dark:text-gray-400 text-sm">
                  {currentInverterIndex + 1}/{allInverters.length}
                </div>
              </>
            )}
          </div>

          {/* Device ID & Status */}
          <div className="text-center mb-4">
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              Unit ID: {inverter.unit_id ?? "—"} ›
            </p>
            <p className="text-gray-400 dark:text-gray-500 text-xs">
              {lastUpdated ? `Updated ${lastUpdated}` : "—"}
            </p>
          </div>

          {/* Tabs: Details | Real Time | Alarms (si Data Logger) */}
          <div className="flex gap-1 mb-4 border-b border-gray-200 dark:border-slate-600">
            <button
              onClick={() => setMainTab("details")}
              className={`flex-1 px-3 py-3 text-sm font-medium ${mainTab === "details" ? "border-b-2 border-purple-500 text-purple-600 dark:text-purple-400" : "text-gray-500 dark:text-gray-400"}`}
            >
              {t("details")}
            </button>
            <button
              onClick={() => setMainTab("realtime")}
              className={`flex-1 px-3 py-3 text-sm font-medium ${mainTab === "realtime" ? "border-b-2 border-purple-500 text-purple-600 dark:text-purple-400" : "text-gray-500 dark:text-gray-400"}`}
            >
              {t("realTime")}
            </button>
            <button
              onClick={() => setMainTab("alarms")}
              className={`flex-1 px-3 py-3 text-sm font-medium flex items-center justify-center gap-1 ${mainTab === "alarms" ? "border-b-2 border-purple-500 text-purple-600 dark:text-purple-400" : "text-gray-500 dark:text-gray-400"}`}
            >
              {t("alarms")}
              <span className={`text-xs px-1.5 py-0.5 rounded ${(alarms?.filter((a) => String(a?.state ?? "OFF").toUpperCase() === "ON").length || 0) > 0 ? "bg-red-500/80 text-white" : "bg-gray-300 dark:bg-slate-600 text-gray-600 dark:text-gray-400"}`}>
                {alarms?.filter((a) => String(a?.state ?? "OFF").toUpperCase() === "ON").length ?? 0}
              </span>
            </button>
          </div>

          {mainTab === "alarms" ? (
            /* Alarms – lista + histori */
            <div className={`space-y-2 ${cardShadowClass}`}>
              <div className={`rounded-xl overflow-hidden ${isDark ? "bg-[#1e293b] border border-slate-700" : "bg-white border border-gray-200"}`}>
                <div className="px-4 py-3 border-b border-gray-200 dark:border-slate-600">
                  <h3 className="font-semibold text-gray-900 dark:text-white">{t("alarms")} ({alarms?.length ?? 0})</h3>
                </div>
                <div className={`px-4 pb-3 max-h-40 overflow-y-auto ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  {(alarms?.length || 0) === 0 ? (
                    <p className="text-sm py-4">{t("noAlarmsInverter")}</p>
                  ) : (
                    alarms.map((a, i) => {
                      const state = (a?.state ?? "OFF").toUpperCase();
                      const isOn = state === "ON";
                      return (
                        <div key={a?.id ?? a?.no ?? i} className={`py-2.5 border-b border-gray-200 dark:border-slate-600 last:border-0 ${isOn ? "bg-red-50/50 dark:bg-red-900/10" : ""}`}>
                          <span className={`text-sm ${isOn ? "text-red-700 dark:text-red-300 font-medium" : "text-gray-900 dark:text-white"}`}>{formatAlarmDisplay(a)}</span>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
              {alarmHistory.length > 0 && (
                <div className={`rounded-xl overflow-hidden ${isDark ? "bg-[#1e293b] border border-slate-700" : "bg-white border border-gray-200"}`}>
                  <div className="px-4 py-2 border-b border-gray-200 dark:border-slate-600">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{t("alarmHistory") || "Histori alarmeve"}</h3>
                  </div>
                  <div className="max-h-48 overflow-y-auto">
                    <table className="w-full text-[11px]">
                      <thead className={isDark ? "bg-slate-800/50 text-gray-400" : "bg-gray-50 text-gray-600"}>
                        <tr><th className="px-2 py-1.5 text-left font-medium">Data/ora</th><th className="px-2 py-1.5 text-left font-medium">Status</th></tr>
                      </thead>
                      <tbody className={isDark ? "text-gray-300" : "text-gray-700"}>
                        {alarmHistory.slice(0, 30).map((h, i) => (
                          <tr key={i} className={i % 2 === 0 ? (isDark ? "bg-[#1e293b]" : "bg-white") : (isDark ? "bg-slate-800/30" : "bg-gray-50/50")}>
                            <td className="px-2 py-1 whitespace-nowrap">{h.ts ? new Date(h.ts).toLocaleString("sq-AL", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—"}</td>
                            <td className={`px-2 py-1 font-medium ${h.active ? "text-red-600 dark:text-red-400" : "text-gray-500 dark:text-gray-400"}`}>{h.active ? (t("alarmActive") || "Aktiv") : (t("alarmInactive") || "Jo aktiv")}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          ) : mainTab === "realtime" ? (
            /* Real Time – grafiku shfaqet vetëm kur prek Real Time */
            <div className="space-y-2">
              <div className={`rounded-xl overflow-hidden border border-gray-200 dark:border-transparent ${cardShadowClass}`}>
                <div className="bg-white dark:bg-[#1e293b] p-4">
                  <h3 className="font-medium text-gray-900 dark:text-white mb-4">{t("energyAnalysis")}</h3>
                  <div className="flex gap-2 mb-4">
                    {["Day", "Month", "Year", "Lifetime"].map((period) => (
                      <button
                        key={period}
                        onClick={() => setChartPeriod(period)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium ${chartPeriod === period ? "bg-[#22c55e] text-white" : "bg-[#e8e8e8] dark:bg-slate-700 text-gray-600 dark:text-gray-300"}`}
                      >
                        {period}
                      </button>
                    ))}
                  </div>
                  {(chartPeriod === "Day" || chartPeriod === "Month" || chartPeriod === "Year") && (
                    <div className="flex items-center justify-center gap-4 mb-4">
                      <input
                        ref={mobileRealtimeDatePickerRef}
                        type="date"
                        max={invTodayStr()}
                        value={dateStr}
                        onChange={(e) => e.target.value && setSelectedChartDate?.(clampInvChartDate(e.target.value, chartPeriod))}
                        className="sr-only"
                        tabIndex={-1}
                        aria-hidden
                      />
                      <input
                        ref={mobileRealtimeMonthPickerRef}
                        type="month"
                        max={invTodayStr().slice(0, 7)}
                        value={dateStr.slice(0, 7)}
                        onChange={(e) => e.target.value && setSelectedChartDate?.(clampInvChartDate(`${e.target.value}-01`, chartPeriod))}
                        className="sr-only"
                        tabIndex={-1}
                        aria-hidden
                      />
                      <button type="button" onClick={() => onChartDateNavigate(-1)} className="text-gray-600 dark:text-gray-400">◀</button>
                      {chartPeriod === "Year" ? (
                        <select
                          value={dateStr.slice(0, 4)}
                          onChange={(e) => setSelectedChartDate?.(clampInvChartDate(`${e.target.value}-01-01`, "Year"))}
                          className="text-sm text-gray-800 dark:text-gray-100 bg-[#e8e8e8] dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-lg px-2 py-1.5 min-w-[5rem]"
                        >
                          {Array.from({ length: Math.max(1, new Date().getFullYear() - 2019) }, (_, i) => 2020 + i)
                            .filter((y) => y <= new Date().getFullYear())
                            .map((y) => (
                              <option key={y} value={y}>{y}</option>
                            ))}
                        </select>
                      ) : (
                        <button
                          type="button"
                          onClick={openMobileInvRealtimePicker}
                          className="text-sm text-gray-700 dark:text-white min-w-[6.5rem] text-center"
                        >
                          {invMobileRealtimeGraphDateDisplay}
                        </button>
                      )}
                      <button type="button" onClick={() => onChartDateNavigate(1)} className="text-gray-600 dark:text-gray-400">▶</button>
                    </div>
                  )}
                  <div className="bg-gray-50 dark:bg-[#1e293b] border-t border-gray-200 dark:border-slate-600/50 p-4">
                    <div className="relative h-44 min-h-[180px]">
                      {chartLoading && (
                        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/70 dark:bg-[#1e293b]/80 text-xs text-gray-600 dark:text-gray-300">
                          …
                        </div>
                      )}
                      <ResponsiveContainer width="100%" height="100%">
                        {chartPeriod === "Day" ? (
                          <AreaChart data={graphData} margin={{ top: 14, right: 12, left: 8, bottom: 8 }}>
                            <defs><linearGradient id="invMobileFill" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#22d3ee" stopOpacity={0.35} /><stop offset="100%" stopColor="#22d3ee" stopOpacity={0} /></linearGradient></defs>
                            <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#334155" : "#e5e7eb"} vertical={false} />
                            <XAxis dataKey={chartDataKey} axisLine={false} tickLine={false} tick={{ fill: isDark ? "#9ca3af" : "#6b7280", fontSize: 9 }} ticks={["00:00", "06:00", "12:00", "18:00"]} />
                            <YAxis axisLine={false} tickLine={false} width={58} padding={{ top: 10, bottom: 8 }} tick={{ fill: isDark ? "#9ca3af" : "#6b7280", fontSize: 10 }} tickFormatter={(v) => `${Number(v).toFixed(0)} kW`} />
                            <Tooltip contentStyle={{ backgroundColor: isDark ? "#1e293b" : "#fff", border: `1px solid ${isDark ? "#334155" : "#e5e7eb"}`, borderRadius: "8px", color: isDark ? "#fff" : "#000" }} labelFormatter={(label) => (label ? `Koha: ${label}` : label)} formatter={(v) => [`${Number(v).toFixed(0)} kW`, t("pvPower")]} />
                            <Area type="linear" dataKey="value" fill="url(#invMobileFill)" stroke="none" />
                            <Line type="linear" dataKey="value" stroke="#22d3ee" strokeWidth={2} dot={false} />
                          </AreaChart>
                        ) : (
                          <BarChart data={graphData} margin={{ top: 14, right: 12, left: 8, bottom: 8 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#334155" : "#e5e7eb"} vertical={false} />
                            <XAxis dataKey={chartDataKey} axisLine={false} tickLine={false} tick={{ fill: isDark ? "#9ca3af" : "#6b7280", fontSize: 10 }} interval="preserveStartEnd" />
                            <YAxis axisLine={false} tickLine={false} width={58} padding={{ top: 10, bottom: 8 }} tick={{ fill: isDark ? "#9ca3af" : "#6b7280", fontSize: 10 }} tickFormatter={(v) => `${Number(v).toFixed(0)} kW`} />
                            <Tooltip contentStyle={{ backgroundColor: isDark ? "#1e293b" : "#fff", border: `1px solid ${isDark ? "#334155" : "#e5e7eb"}`, borderRadius: "8px", color: isDark ? "#fff" : "#000" }} formatter={(v) => [`${Number(v).toFixed(0)} kW`, t("pvPower")]} />
                            <Bar dataKey="value" fill="#22d3ee" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        )}
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* Details – Power Gauge and Stats */
            <>
              {/* Power Gauge and Stats - Same style as Devices page */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                {/* PV Power Gauge */}
                <div
                  className={`bg-white dark:bg-[#1e293b] rounded-xl p-4 ${cardShadowClass}`}
                >
                  <div className="relative w-24 h-24 mx-auto mb-2">
                    <svg className="w-24 h-24 transform -rotate-90">
                      <circle
                        cx="48"
                        cy="48"
                        r="40"
                        stroke="#e5e7eb"
                        className="dark:stroke-gray-700"
                        strokeWidth="8"
                        fill="none"
                      />
                      <circle
                        cx="48"
                        cy="48"
                        r="40"
                        stroke="#22d3ee"
                        strokeWidth="8"
                        fill="none"
                        strokeDasharray={`${(pvPercent / 100) * 251} 251`}
                        strokeLinecap="round"
                      />
                    </svg>
                    <div
                      className="absolute inset-0 flex flex-col items-center justify-center"
                      style={{ marginTop: "6px" }}
                    >
                      <span className="text-sm font-bold text-gray-900 dark:text-white leading-tight">
                        {pvPowerKw < 1000 ? safeToFixed(pvPowerKw, 2) : safeToFixed(pvPowerKw, 0)}
                      </span>
                      <span className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">
                        kW
                      </span>
                    </div>
                  </div>
                  <p className="text-center text-xs text-cyan-500">{t("pvPower")}</p>
                </div>

                {/* Stats Cards */}
                <div className="space-y-3">
                  <div
                    className={`bg-white dark:bg-[#1e293b] rounded-xl p-3 ${cardShadowClass}`}
                  >
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {t("todayPvYield")}
                    </p>
                    <p className="text-xl font-bold text-cyan-500">
                      {safeToFixed(todayYieldNum, 2)}
                      <span className="text-sm ml-1">MWh</span>
                    </p>
                  </div>
                  <div
                    className={`bg-white dark:bg-[#1e293b] rounded-xl p-3 ${cardShadowClass}`}
                  >
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {t("totalPvYield")}
                    </p>
                    <p className="text-xl font-bold text-gray-900 dark:text-white">
                      {safeToFixed(totalYieldNum, 2)}
                      <span className="text-sm ml-1">MWh</span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Detailed Parameters – accordion në të njëjtën faqe (mobile) */}
              <div className="space-y-3 mb-4">
                {inverterDetailSections.map((section) => (
                  <div
                    key={section.name}
                    className={`bg-white dark:bg-[#1e293b] rounded-xl overflow-hidden ${cardShadowClass}`}
                  >
                    <button
                      onClick={() =>
                        setExpandedParam(
                          expandedParam === section.name ? null : section.name,
                        )
                      }
                      className="w-full flex items-center justify-between p-4"
                    >
                      <span className="font-medium text-gray-900 dark:text-white">
                        {t(SECTION_KEYS[section.name] || section.name)}
                      </span>
                      <svg
                        className={`w-5 h-5 text-gray-400 transition-transform ${expandedParam === section.name ? "rotate-180" : ""}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>
                    {expandedParam === section.name && (
                      <div className="border-t border-gray-100 dark:border-slate-700 pt-3">
                        <div className="px-4 pb-4 space-y-2 max-h-[200px] overflow-y-auto overscroll-contain">
                          {section.name === "PV Voltage" ||
                            section.name === "PV Current"
                            ? section.items.flatMap((item, idx) => {
                              const suffix =
                                section.name === "PV Voltage"
                                  ? " voltage"
                                  : " current";
                              const pvPart = item.label.replace(suffix, "");
                              const pvNames = pvPart.split("/").filter(Boolean);
                              const vals = String(item.value)
                                .split(",")
                                .map((v) => v.trim());
                              const rows = [];
                              for (
                                let i = 0;
                                i < Math.max(pvNames.length, vals.length);
                                i += 3
                              ) {
                                const pvChunk = pvNames.slice(i, i + 3);
                                const valChunk = vals.slice(i, i + 3);
                                rows.push(
                                  <div
                                    key={`${idx}-${i}`}
                                    className="flex justify-between items-center text-sm gap-2"
                                  >
                                    <span className="text-gray-500 dark:text-gray-400">
                                      {pvChunk.join(", ")}
                                    </span>
                                    <span className="text-gray-900 dark:text-white font-medium">
                                      {valChunk.join(", ")}
                                    </span>
                                  </div>,
                                );
                              }
                              return rows;
                            })
                            : section.items.map((item, idx) => (
                              <div
                                key={idx}
                                className="flex justify-between items-start text-sm gap-3"
                              >
                                <span className="text-gray-500 dark:text-gray-400 shrink-0 text-left">
                                  {t(LABEL_KEYS[item.label] || item.label)}
                                </span>
                                <span className="text-gray-900 dark:text-white font-medium text-right min-w-0 break-words">
                                  {item.value}
                                </span>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================

const InverterDetail = () => {
  const { id: deviceId, inverterId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();
  const [inverter, setInverter] = useState(null);
  const [allInverters, setAllInverters] = useState([]);
  const [dataLogger, setDataLogger] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [readings, setReadings] = useState([]);
  const [alarms, setAlarms] = useState([]);
  const [alarmHistory, setAlarmHistory] = useState([]);
  const [chartReadings, setChartReadings] = useState(null); // null = përdor readings, [] = fetch në progres, [...]= historik
  const [chartLoading, setChartLoading] = useState(false);
  const [monthlyYieldMWhFromApi, setMonthlyYieldMWhFromApi] = useState(null); // nga fetch muaji i plotë (dita 1 deri sot)
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDark, setIsDark] = useState(
    document.documentElement.classList.contains("dark"),
  );

  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const clickCountRef = useRef(0);
  const clickTimerRef = useRef(null);
  const chartLongPressRef = useRef(null);
  const [chartColor, setChartColor] = useState("#22d3ee"); // cyan default
  const chartColors = [
    "#22d3ee",
    "#8b5cf6",
    "#22c55e",
    "#f59e0b",
    "#ef4444",
    "#ec4899",
  ];
  const [chartColorIndex, setChartColorIndex] = useState(0);
  const todayStr = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };
  const storageKey = inverterId ? `chart-inv-${inverterId}` : null;

  const [webChartPeriod, setWebChartPeriod] = useState(() => {
    if (typeof window !== "undefined" && storageKey) {
      try {
        const s = localStorage.getItem(storageKey);
        if (s) {
          const p = JSON.parse(s);
          if (p?.webChartPeriod) return p.webChartPeriod;
        }
      } catch (_) { }
    }
    return "Day";
  });
  const [webSelectedDate, setWebSelectedDate] = useState(() => {
    if (typeof window !== "undefined" && storageKey) {
      try {
        const s = localStorage.getItem(storageKey);
        if (s) {
          const p = JSON.parse(s);
          if (p?.webSelectedDate) return p.webSelectedDate;
        }
      } catch (_) { }
    }
    return todayStr();
  });
  const [analysisPeriod, setAnalysisPeriod] = useState(() => {
    if (typeof window !== "undefined" && storageKey) {
      try {
        const s = localStorage.getItem(storageKey);
        if (s) {
          const p = JSON.parse(s);
          if (p?.analysisPeriod) return p.analysisPeriod;
        }
      } catch (_) { }
    }
    return "week";
  });
  const [activeTab, setActiveTab] = useState(() => {
    if (typeof window !== "undefined" && storageKey) {
      try {
        const s = localStorage.getItem(storageKey);
        if (s) {
          const p = JSON.parse(s);
          if (p?.activeTab) return p.activeTab;
        }
      } catch (_) { }
    }
    return "details";
  });

  const isSuperAdmin = user?.role === "SUPERADMIN";

  const saveChartPrefs = useCallback(() => {
    if (storageKey && typeof window !== "undefined") {
      try {
        localStorage.setItem(storageKey, JSON.stringify({
          webSelectedDate,
          webChartPeriod,
          analysisPeriod,
          activeTab,
        }));
      } catch (_) { }
    }
  }, [storageKey, webSelectedDate, webChartPeriod, analysisPeriod, activeTab]);

  // Detect mobile
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    saveChartPrefs();
  }, [saveChartPrefs]);

  useEffect(() => {
    fetchInverter();
    fetchAllInverters();
    fetchDataLogger();

    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains("dark"));
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch helpers defined below; ids drive reload
  }, [inverterId, deviceId]);

  // Refresh readings çdo 2 minuta (për të parë të dhënat e reja sa më shpejt pas sync 5-min)
  useEffect(() => {
    if (!inverterId) return;
    const interval = setInterval(() => {
      const getToken = () => localStorage.getItem("access_token");
      axios
        .get(
          `${BACKEND_URL}/api/devices/${inverterId}/pi-inverter-readings?limit=300`,
          { headers: { Authorization: `Bearer ${getToken()}` } },
        )
        .then((res) => setReadings(res.data?.readings ?? []))
        .catch(() => { });
    }, 2 * 60 * 1000); // 2 minuta
    return () => clearInterval(interval);
  }, [inverterId]);

  // Cache i shkurtër për grafik – 2 min (eviton refetch, më pak ngarkesë API/mobile)
  const chartCacheRef = useRef({ key: "", data: null, ts: 0 });
  const realtimeDatePickerRef = useRef(null);
  const realtimeMonthPickerRef = useRef(null);

  // Për grafik: fetch me debounce + anulim, pa overload në API / mobile
  useEffect(() => {
    if (!inverterId || !webSelectedDate) return;
    const sel = new Date(webSelectedDate);
    let dateFrom = "";
    let dateTo = "";
    if (webChartPeriod === "Day") {
      dateFrom = `${webSelectedDate}T00:00:00`;
      dateTo = `${webSelectedDate}T23:59:59`;
    } else if (webChartPeriod === "Month") {
      const y = sel.getFullYear();
      const m = sel.getMonth();
      dateFrom = `${y}-${String(m + 1).padStart(2, "0")}-01T00:00:00`;
      const lastDay = new Date(y, m + 1, 0).getDate();
      dateTo = `${y}-${String(m + 1).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}T23:59:59`;
    } else if (webChartPeriod === "Year") {
      dateFrom = `${sel.getFullYear()}-01-01T00:00:00`;
      dateTo = `${sel.getFullYear()}-12-31T23:59:59`;
    } else if (webChartPeriod === "Lifetime") {
      const now = new Date();
      dateFrom = "2020-01-01T00:00:00";
      dateTo = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}T23:59:59`;
    } else {
      setChartReadings(null);
      return;
    }
    const chartKey = `${inverterId}|${dateFrom}|${dateTo}`;
    const cached = chartCacheRef.current;
    if (cached.key === chartKey && cached.data !== null && Date.now() - cached.ts < CHART_CACHE_TTL_MS) {
      setChartReadings(cached.data);
      return;
    }
    setChartLoading(true);
    const controller = new AbortController();
    const timer = setTimeout(() => {
      const getToken = () => localStorage.getItem("access_token");
      axios
        .get(
          `${BACKEND_URL}/api/devices/${inverterId}/pi-inverter-readings?date_from=${encodeURIComponent(dateFrom)}&date_to=${encodeURIComponent(dateTo)}`,
          { headers: { Authorization: `Bearer ${getToken()}` }, signal: controller.signal },
        )
        .then((res) => {
          const data = res.data?.readings ?? [];
          chartCacheRef.current = { key: chartKey, data, ts: Date.now() };
          setChartReadings(data);
          setChartLoading(false);
        })
        .catch((err) => {
          setChartLoading(false);
          if (axios.isCancel(err)) return;
          setChartReadings([]);
        });
    }, 200);
    return () => {
      clearTimeout(timer);
      controller.abort();
      setChartLoading(false);
    };
  }, [inverterId, webSelectedDate, webChartPeriod]);

  // Monthly PV Yield: backend e llogarit (dita 1 e muajit deri sot) – frontend vetëm e merr
  useEffect(() => {
    if (!inverterId) return;
    const getToken = () => localStorage.getItem("access_token");
    axios
      .get(`${BACKEND_URL}/api/devices/${inverterId}/monthly-pv-yield`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      })
      .then((res) => {
        const mwh = res.data?.monthly_yield_mwh;
        setMonthlyYieldMWhFromApi(mwh != null ? Number(mwh) : null);
      })
      .catch(() => setMonthlyYieldMWhFromApi(null));
  }, [inverterId]);

  const getToken = () => localStorage.getItem("access_token");

  const fetchInverter = async () => {
    if (!inverterId) return;
    try {
      const headers = { Authorization: `Bearer ${getToken()}` };
      const [invRes, readingsRes, alarmsPiRes, alarmsApiRes, companiesRes] = await Promise.all([
        cachedAxiosGet(axios, `${BACKEND_URL}/api/devices/inverters/${inverterId}`, { headers }, { ttlMs: 90_000 }),
        axios
          .get(
            `${BACKEND_URL}/api/devices/${inverterId}/pi-inverter-readings?limit=300`,
            { headers },
          )
          .catch(() => ({ data: { readings: [] } })),
        axios.get(`${BACKEND_URL}/api/devices/${inverterId}/pi-alarms-inverter?limit=50`, { headers }).catch(() => ({ data: { alarms: [] } })),
        axios.get(`${BACKEND_URL}/api/alarms?device_id=${inverterId}&device_type=INVERTER&limit=50`, { headers }).catch(() => ({ data: [] })),
        cachedAxiosGet(axios, `${BACKEND_URL}/api/companies`, { headers }, { ttlMs: 120_000 }).catch(() => ({ data: [] })),
      ]);
      setInverter(invRes.data);
      setReadings(readingsRes.data?.readings ?? []);
      setCompanies(Array.isArray(companiesRes.data) ? companiesRes.data : []);

      const piAlarmsRaw = alarmsPiRes.data?.alarms ?? [];
      const piAlarms = [];
      piAlarmsRaw.forEach((doc) => {
        const list = Array.isArray(doc?.payload?.alarms) ? doc.payload.alarms : [];
        list.forEach((a) => piAlarms.push({ ...a, _ts: doc?.ts }));
        if (list.length === 0 && doc?.payload && (doc.payload.name != null || doc.payload.message != null)) {
          piAlarms.push({ ...doc.payload, _ts: doc?.ts });
        }
      });
      const apiAlarms = Array.isArray(alarmsApiRes.data) ? alarmsApiRes.data : [];
      const merged = apiAlarms.map((a) => ({ name: a?.message, message: a?.message, state: a?.is_active ? "ON" : "OFF", ...a }));
      piAlarms.forEach((a) => {
        if (!merged.some((m) => (m?.id && m.id === a?.id) || (m?.no === a?.no && String(m?.name || "").trim() === String(a?.name || a?.message || "").trim()))) merged.push(a);
      });
      setAlarms(merged);
      const history = (piAlarmsRaw || []).map((doc) => {
        const pl = doc?.payload || {};
        const alarmsList = Array.isArray(pl.alarms) ? pl.alarms : [];
        const anyOn = alarmsList.some((a) => String(a?.state || "").toUpperCase() === "ON");
        return { ts: doc?.ts, active: !!anyOn };
      });
      setAlarmHistory(history);
    } catch (error) {
      console.error("Error fetching inverter:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDataLogger = async () => {
    try {
      const response = await cachedAxiosGet(
        axios,
        `${BACKEND_URL}/api/devices/data-loggers/${deviceId}`,
        { headers: { Authorization: `Bearer ${getToken()}` } },
        { ttlMs: 90_000 },
      );
      setDataLogger(response.data);
    } catch (error) {
      console.error("Error fetching data logger:", error);
    }
  };

  const getCurrentCompany = () => {
    if (!dataLogger || !companies.length) return null;
    return companies.find((c) => c.id === dataLogger.company_id);
  };

  const currentCompany = getCurrentCompany();

  const fetchAllInverters = async () => {
    try {
      const response = await cachedAxiosGet(
        axios,
        `${BACKEND_URL}/api/devices/inverters?data_logger_id=${deviceId}`,
        { headers: { Authorization: `Bearer ${getToken()}` } },
        { ttlMs: 60_000 },
      );
      setAllInverters(response.data);
      const idx = response.data.findIndex((inv) => inv.id === inverterId);
      if (idx !== -1) setCurrentIndex(idx);
    } catch (error) {
      console.error("Error fetching inverters:", error);
    }
  };

  const handlePhotoClick = () => {
    if (!isSuperAdmin) return;
    clickCountRef.current += 1;
    if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
    if (clickCountRef.current >= 3) {
      clickCountRef.current = 0;
      setShowPhotoModal(true);
    } else {
      clickTimerRef.current = setTimeout(() => {
        clickCountRef.current = 0;
      }, 500);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await axios.post(
        `${BACKEND_URL}/api/upload/photo/inverter/${inverterId}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
            "Content-Type": "multipart/form-data",
          },
        },
      );
      fetchInverter();
      setShowPhotoModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Gabim gjatë ngarkimit të fotos");
    } finally {
      setUploading(false);
    }
  };

  // Long press on chart to change color (3 seconds)
  const handleChartLongPressStart = () => {
    chartLongPressRef.current = setTimeout(() => {
      const newIndex = (chartColorIndex + 1) % chartColors.length;
      setChartColorIndex(newIndex);
      setChartColor(chartColors[newIndex]);
    }, 3000);
  };

  const handleChartLongPressEnd = () => {
    if (chartLongPressRef.current) {
      clearTimeout(chartLongPressRef.current);
      chartLongPressRef.current = null;
    }
  };

  const latestReading = readings.length > 0 ? readings[0] : null;
  const mockData = buildInverterMockDataFromPayload(
    latestReading?.payload,
    inverter,
  );
  const pvPowerKwWeb = (Number(mockData?.pv_power) || 0) * 1000;
  const capacityKw = Number(inverter?.power_kw) || Number(inverter?.rated_power_kw) || 0;
  const hasRealtimeTelemetry = hasInverterTelemetry(latestReading?.payload);
  // Monthly PV Yield: mbledh energy_day_kwh për çdo ditë – për çdo ditë marrim leximin me vlerën më të madhe (max), jo më të fundit, që natën (0) të mos mbizotërojë
  const monthlyYieldMWh = useMemo(() => {
    const list = readings;
    if (!list.length) return null;
    const byDay = {};
    for (const r of list) {
      const d = new Date(r.ts);
      const dayKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      const dayKwh = getDayEnergyKwhFromPayload(r);
      if (dayKwh == null) continue;
      const prev = byDay[dayKey];
      const prevKwh = prev != null ? getDayEnergyKwhFromPayload(prev) : null;
      if (prevKwh == null || dayKwh > prevKwh) byDay[dayKey] = r;
    }
    let sumKwh = 0;
    for (const r of Object.values(byDay)) {
      const dayKwh = getDayEnergyKwhFromPayload(r);
      if (dayKwh != null) sumKwh += dayKwh;
    }
    return sumKwh > 0 ? sumKwh / 1000 : null;
  }, [readings]);

  const paramSections = buildInverterParamSectionsFromPayload(
    latestReading?.payload,
  );
  const inverterDetailSections = buildInverterDetailSections(
    latestReading?.payload,
  );
  const lastUpdated = formatTimeAgo(latestReading?.ts) || null;

  // Të dhëna grafikësh nga pi_inverter_readings (reale)
  const readingsForChart = chartReadings !== null ? chartReadings : readings;
  const analysisChartData = buildAnalysisChartData(readingsForChart, analysisPeriod, webSelectedDate);
  const realTimeChartData = buildRealTimeChartData(readingsForChart, webSelectedDate, webChartPeriod);
  const webChartDataKey = webChartPeriod === "Day" ? "time" : "name";

  const changeWebDate = (delta) => {
    setWebSelectedDate((prev) => {
      const base = prev || invTodayStr();
      const d = new Date(`${base}T12:00:00`);
      if (Number.isNaN(d.getTime())) return invTodayStr();
      if (webChartPeriod === "Day") d.setDate(d.getDate() + delta);
      else if (webChartPeriod === "Month") d.setMonth(d.getMonth() + delta);
      else if (webChartPeriod === "Year") d.setFullYear(d.getFullYear() + delta);
      return clampInvChartDate(
        `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`,
        webChartPeriod,
      );
    });
  };

  const changeAnalysisDate = (delta) => {
    setWebSelectedDate((prev) => {
      const d = new Date(prev || new Date());
      if (analysisPeriod === "day") d.setDate(d.getDate() + delta);
      else if (analysisPeriod === "week") d.setDate(d.getDate() + 7 * delta);
      else if (analysisPeriod === "month") d.setMonth(d.getMonth() + delta);
      else if (analysisPeriod === "year") d.setFullYear(d.getFullYear() + delta);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    });
  };

  const today = new Date();
  const dateStr = webSelectedDate || `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  const d = new Date(webSelectedDate || dateStr);
  const realtimeGraphDateDisplay =
    webChartPeriod === "Day"
      ? dateStr
      : webChartPeriod === "Month"
        ? (webSelectedDate ? webSelectedDate.slice(0, 7) : "")
        : webChartPeriod === "Year"
          ? (webSelectedDate ? webSelectedDate.slice(0, 4) : "")
          : "";
  const openWebRealtimePeriodPicker = () => {
    if (webChartPeriod === "Day") {
      realtimeDatePickerRef.current?.showPicker?.();
      realtimeDatePickerRef.current?.click();
    } else if (webChartPeriod === "Month") {
      realtimeMonthPickerRef.current?.showPicker?.();
      realtimeMonthPickerRef.current?.click();
    }
  };
  const weekStart = new Date(d);
  weekStart.setDate(weekStart.getDate() - 6);
  const analysisDateDisplay =
    analysisPeriod === "day"
      ? dateStr
      : analysisPeriod === "week"
        ? `${String(weekStart.getDate()).padStart(2, "0")}.${String(weekStart.getMonth() + 1).padStart(2, "0")} ~ ${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}`
        : analysisPeriod === "month"
          ? `${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()}`
          : analysisPeriod === "year"
            ? String(d.getFullYear())
            : "Total";

  // Theme colors - matching our app's dark theme
  const bgMain = isDark ? "#0f172a" : "#f8fafc";
  const bgCard = isDark ? "#1e293b" : "#ffffff";
  const textPrimary = isDark ? "text-white" : "text-gray-900";
  const textSecondary = isDark ? "text-gray-400" : "text-gray-500";
  const borderColor = isDark ? "border-[#334155]" : "border-gray-200";

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Duke ngarkuar...</div>
        </div>
      </Layout>
    );
  }

  if (!inverter) {
    return (
      <Layout>
        <div className="p-6 text-center">
          <p className="text-xl text-red-600">{t("inverterNotFound")}</p>
          <button
            onClick={() => navigate(`/devices/${deviceId}`)}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg"
          >
            {t("back")}
          </button>
        </div>
      </Layout>
    );
  }

  // MOBILE VIEW - Return early if mobile
  if (isMobile) {
    return (
      <MobileInverterView
        inverter={inverter}
        dataLogger={dataLogger}
        currentCompany={currentCompany}
        navigate={navigate}
        mockData={mockData}
        isDark={isDark}
        allInverters={allInverters}
        companies={companies}
        inverterDetailSections={inverterDetailSections}
        lastUpdated={lastUpdated}
        alarms={alarms}
        alarmHistory={alarmHistory}
        chartPeriod={webChartPeriod}
        setChartPeriod={setWebChartPeriod}
        selectedChartDate={webSelectedDate}
        setSelectedChartDate={setWebSelectedDate}
        onChartDateNavigate={changeWebDate}
        readingsForChart={readingsForChart}
        chartLoading={chartLoading}
        t={t}
      />
    );
  }

  // WEB VIEW (Desktop) - Original design
  return (
    <Layout>
      <div
        className="h-full flex flex-col overflow-hidden"
        style={{ backgroundColor: bgMain }}
      >
        {/* Header */}
        <div
          className={`flex-shrink-0 border-b ${borderColor} px-4 py-3`}
          style={{ backgroundColor: bgCard }}
        >
          <div className="flex items-center gap-3">
            <button
              onClick={() =>
                currentCompany
                  ? navigate(`/devices?company=${currentCompany.id}`)
                  : navigate("/devices")
              }
              className={`${textSecondary} hover:opacity-80`}
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <span className={`font-semibold ${textPrimary}`}>
              {inverter.name || "R"}
            </span>
            <svg
              className="w-4 h-4 text-yellow-500"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            <span className={`text-sm ${textSecondary}`}>
              Unit ID: {inverter.unit_id ?? "—"}
            </span>
            <button className={`ml-1 ${textSecondary} hover:opacity-80`}>
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                />
              </svg>
            </button>
            <div className="flex-1"></div>
            {lastUpdated && (
              <span className={`text-sm ${textSecondary}`}>
                Updated {lastUpdated}
              </span>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left Panel - Device Info (stil i njëjtë si chart djathtas: rounded-xl, border, shadow) */}
          <div
            className={`w-72 flex flex-col overflow-y-auto flex-shrink-0 p-4`}
            style={{ backgroundColor: bgMain }}
          >
            <div
              className="flex-1 rounded-xl border shadow-sm overflow-hidden flex flex-col"
              style={{
                backgroundColor: bgCard,
                borderColor: isDark ? "#334155" : "#f3f4f6",
              }}
            >
              {/* Device Photo */}
              <div
                className={`p-6 border-b ${borderColor}`}
                onClick={handlePhotoClick}
              >
                <div
                  className={`w-full h-40 rounded-xl flex items-center justify-center overflow-hidden ${isDark ? "bg-[#1e293b]" : "bg-white"}`}
                >
                  {inverter.photo_url ? (
                    <img
                      src={`${BACKEND_URL}${inverter.photo_url}`}
                      alt={inverter.name}
                      className="max-h-full max-w-full object-contain"
                    />
                  ) : (
                    <svg
                      className={`w-24 h-24 ${isDark ? "text-gray-600" : "text-gray-300"}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1}
                        d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z"
                      />
                    </svg>
                  )}
                </div>
                <h2
                  className={`text-2xl font-bold text-center mt-4 ${textPrimary}`}
                >
                  {inverter.name || "R"}
                </h2>
              </div>

              {/* Divider */}
              <div className={`border-b ${borderColor}`}></div>

              {/* Device Information */}
              <div className="p-4 flex-1">
                <h3 className={`font-semibold ${textPrimary} mb-4`}>
                  Device Information
                </h3>

                <div className="space-y-3">
                  <div className="flex justify-between items-center gap-2">
                    <span className={`${textSecondary} shrink-0`}>State:</span>
                    <span className="px-3 py-1.5 bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 rounded-lg text-sm font-medium flex items-center gap-1.5 min-w-0 flex-1 justify-end">
                      <svg
                        className="w-3.5 h-3.5 shrink-0"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
                      </svg>
                      <span
                        className="truncate"
                        title={mockData?.state || "Normal"}
                      >
                        {mockData?.state || "Normal"}
                      </span>
                    </span>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className={textSecondary}>Unit ID:</span>
                    <span className={textPrimary}>
                      {inverter.unit_id ?? "—"}
                    </span>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className={textSecondary}>Plant:</span>
                    <button
                      className={`flex items-center gap-1 ${textPrimary} hover:opacity-80`}
                    >
                      <span>
                        {currentCompany?.name || dataLogger?.name || "N/A"}
                      </span>
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </button>
                  </div>

                  <div className="flex justify-between">
                    <span className={textSecondary}>Model:</span>
                    <span className={textPrimary}>
                      {inverter.model || mockData.model}
                    </span>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className={textSecondary}>Data Logger:</span>
                    <span className={`${textPrimary} truncate`} title={currentCompany?.name}>
                      {inverter?.name || inverter?.model || inverter?.brand || dataLogger?.name || "—"} — {currentCompany?.name || "—"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Panel - Stats and Charts */}
          <div className="flex-1 flex flex-col overflow-hidden p-4">
            {/* Stats Cards Row */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4 flex-shrink-0">
              {/* PV Power */}
              <div
                className="rounded-xl p-4 border shadow-sm"
                style={{
                  backgroundColor: bgCard,
                  borderColor: isDark ? "#334155" : "#f3f4f6",
                }}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <p className={`text-2xl font-bold ${textPrimary}`}>
                      {pvPowerKwWeb < 1000 ? safeToFixed(pvPowerKwWeb, 2) : safeToFixed(pvPowerKwWeb, 0)}{" "}
                      <span className="text-base font-normal">kW</span>
                    </p>
                    <p className={`text-sm ${textSecondary} mt-1`}>{t("pvPower")}</p>
                  </div>
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${isDark ? "bg-purple-900/30" : "bg-purple-50"}`}
                  >
                    <svg
                      className="w-5 h-5 text-purple-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
                      />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Total inverter (capacity) */}
              <div
                className="rounded-xl p-4 border shadow-sm"
                style={{
                  backgroundColor: bgCard,
                  borderColor: isDark ? "#334155" : "#f3f4f6",
                }}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <p className={`text-2xl font-bold ${textPrimary}`}>
                      {hasRealtimeTelemetry && capacityKw > 0 ? (
                        <>
                          {safeToFixed(capacityKw / 1000, 2)}{" "}
                          <span className="text-base font-normal">MW</span>
                        </>
                      ) : (
                        "—"
                      )}
                    </p>
                    <p className={`text-sm ${textSecondary} mt-1`}>
                      {t("totalInverter") || "Total inverter"}
                    </p>
                  </div>
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${isDark ? "bg-purple-900/30" : "bg-purple-50"}`}
                  >
                    <svg className="w-5 h-5 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" /></svg>
                  </div>
                </div>
              </div>

              {/* Today PV Yield */}
              <div
                className="rounded-xl p-4 border shadow-sm"
                style={{
                  backgroundColor: bgCard,
                  borderColor: isDark ? "#334155" : "#f3f4f6",
                }}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <p className={`text-2xl font-bold ${textPrimary}`}>
                      {safeToFixed(mockData?.today_yield, 2)}{" "}
                      <span className="text-base font-normal">MWh</span>
                    </p>
                    <p className={`text-sm ${textSecondary} mt-1`}>
                      {t("todayPvYield")}
                    </p>
                  </div>
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${isDark ? "bg-purple-900/30" : "bg-purple-50"}`}
                  >
                    <svg
                      className="w-5 h-5 text-purple-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                      />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Monthly PV Yield – nga API fillimi i muajit deri sot (energy_total_kwh last − first) */}
              <div
                className="rounded-xl p-4 border shadow-sm"
                style={{
                  backgroundColor: bgCard,
                  borderColor: isDark ? "#334155" : "#f3f4f6",
                }}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <p className={`text-2xl font-bold ${textPrimary}`}>
                      {safeToFixed(monthlyYieldMWhFromApi ?? monthlyYieldMWh ?? mockData?.monthly_yield, 2)}{" "}
                      <span className="text-base font-normal">MWh</span>
                    </p>
                    <p className={`text-sm ${textSecondary} mt-1`}>
                      {t("monthlyPvYield")}
                    </p>
                  </div>
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${isDark ? "bg-purple-900/30" : "bg-purple-50"}`}
                  >
                    <svg
                      className="w-5 h-5 text-purple-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                      />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Total PV Yield */}
              <div
                className="rounded-xl p-4 border shadow-sm"
                style={{
                  backgroundColor: bgCard,
                  borderColor: isDark ? "#334155" : "#f3f4f6",
                }}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <p className={`text-2xl font-bold ${textPrimary}`}>
                      {safeToFixed(mockData?.total_yield, 2)}{" "}
                      <span className="text-base font-normal">MWh</span>
                    </p>
                    <p className={`text-sm ${textSecondary} mt-1`}>
                      {t("totalPvYield")}
                    </p>
                  </div>
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${isDark ? "bg-purple-900/30" : "bg-purple-50"}`}
                  >
                    <svg
                      className="w-5 h-5 text-purple-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            {/* Tabs: Details, Real-time, Energy Analysis, Alarms */}
            <div className={`border-b ${borderColor} mb-4 flex-shrink-0`}>
              <div className="flex gap-6">
                <button
                  onClick={() => setActiveTab("details")}
                  className={`pb-3 text-sm font-medium border-b-2 transition ${activeTab === "details"
                    ? "border-purple-500 text-purple-500"
                    : `border-transparent ${textSecondary} hover:opacity-80`
                    }`}
                >
                  {t("details")}
                </button>
                <button
                  onClick={() => setActiveTab("realtime")}
                  className={`pb-3 text-sm font-medium border-b-2 transition ${activeTab === "realtime"
                    ? "border-purple-500 text-purple-500"
                    : `border-transparent ${textSecondary} hover:opacity-80`
                    }`}
                >
                  {t("realTime")}
                </button>
                <button
                  onClick={() => setActiveTab("energyanalysis")}
                  className={`pb-3 text-sm font-medium border-b-2 transition ${activeTab === "energyanalysis"
                    ? "border-purple-500 text-purple-500"
                    : `border-transparent ${textSecondary} hover:opacity-80`
                    }`}
                >
                  {t("energyAnalysis")}
                </button>
                <button
                  onClick={() => setActiveTab("alarms")}
                  className={`pb-3 text-sm font-medium border-b-2 transition flex items-center gap-1.5 ${activeTab === "alarms"
                    ? "border-purple-500 text-purple-500"
                    : `border-transparent ${textSecondary} hover:opacity-80`
                    }`}
                >
                  {t("alarms")}
                  <span className={`text-xs px-1.5 py-0.5 rounded ${(alarms?.filter((a) => String(a?.state ?? "OFF").toUpperCase() === "ON").length || 0) > 0 ? "bg-red-500/80 text-white" : "bg-gray-200 dark:bg-slate-600 text-gray-600 dark:text-gray-400"}`}>
                    {alarms?.filter((a) => String(a?.state ?? "OFF").toUpperCase() === "ON").length ?? 0}
                  </span>
                </button>
              </div>
            </div>

            {/* Content Area */}
            <div className={`flex-1 flex flex-col min-h-0 ${activeTab === "alarms" ? "overflow-visible" : "overflow-hidden"}`}>
              {activeTab === "alarms" ? (
                <div className={`flex-1 rounded-xl border p-4 flex flex-col overflow-y-auto`} style={{ backgroundColor: bgCard, borderColor: isDark ? "#334155" : "#f3f4f6" }}>
                  <h3 className={`font-semibold ${textPrimary} mb-4`}>{t("alarms")} ({alarms?.length ?? 0})</h3>
                  {(alarms?.length || 0) === 0 ? (
                    <p className={`${textSecondary} text-sm`}>{t("noAlarmsInverter")}</p>
                  ) : (
                    <ul className="grid grid-cols-2 gap-x-4 gap-y-1 mb-6">
                      {alarms.map((a, i) => {
                        const state = (a?.state ?? "OFF").toUpperCase();
                        const isOn = state === "ON";
                        return (
                          <li key={a?.id ?? a?.no ?? i} className={`flex items-center py-3 px-3 rounded-lg border ${borderColor} text-sm ${isOn ? "bg-red-50/50 dark:bg-red-900/10 border-red-200 dark:border-red-900/30" : ""}`}>
                            <span className={isOn ? "text-red-700 dark:text-red-300 font-medium" : textPrimary}>{formatAlarmDisplay(a)}</span>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                  {alarmHistory.length > 0 && (
                    <>
                      <h3 className={`text-sm font-semibold ${textPrimary} mb-2`}>{t("alarmHistory") || "Histori alarmeve"}</h3>
                      <div className="rounded-lg border overflow-hidden" style={{ borderColor: isDark ? "#334155" : "#e5e7eb" }}>
                        <table className="w-full text-sm">
                          <thead className={isDark ? "bg-slate-800/50" : "bg-gray-50"}>
                            <tr><th className={`px-3 py-2 text-left font-medium ${textSecondary}`}>Data/ora</th><th className={`px-3 py-2 text-left font-medium ${textSecondary}`}>Status</th></tr>
                          </thead>
                          <tbody className={textPrimary}>
                            {alarmHistory.slice(0, 30).map((h, i) => (
                              <tr key={i} className={i % 2 === 0 ? (isDark ? "bg-[#1e293b]" : "bg-white") : (isDark ? "bg-slate-800/30" : "bg-gray-50/50")}>
                                <td className="px-3 py-2 whitespace-nowrap">{h.ts ? new Date(h.ts).toLocaleString("sq-AL", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—"}</td>
                                <td className={`px-3 py-2 font-medium ${h.active ? "text-red-600 dark:text-red-400" : "text-gray-500 dark:text-gray-400"}`}>{h.active ? (t("alarmActive") || "Aktiv") : (t("alarmInactive") || "Jo aktiv")}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </div>
              ) : activeTab === "details" ? (
                <div className="flex flex-col overflow-y-auto min-h-0">
                  {/* Details – Accordion dropdown (27 parametra, dizajn si Data Logger) */}
                  <div
                    className={`rounded-xl border p-4 flex-shrink-0`}
                    style={{
                      backgroundColor: bgCard,
                      borderColor: isDark ? "#334155" : "#f3f4f6",
                    }}
                  >
                    <Accordion
                      type="multiple"
                      defaultValue={[]}
                      className="w-full"
                    >
                      {inverterDetailSections.map((section) => (
                        <AccordionItem
                          key={section.name}
                          value={section.name}
                          className={`border-b ${borderColor}`}
                        >
                          <AccordionTrigger
                            className={`${textPrimary} hover:no-underline py-3`}
                          >
                            {t(SECTION_KEYS[section.name] || section.name)}
                          </AccordionTrigger>
                          <AccordionContent>
                            <div className="space-y-2">
                              {section.items.map((item, idx) => (
                                <div
                                  key={idx}
                                  className="flex justify-between items-center py-1.5 gap-4"
                                >
                                  <span className={`text-sm ${textSecondary}`}>
                                    {t(LABEL_KEYS[item.label] || item.label)}
                                  </span>
                                  <span
                                    className={`text-sm font-medium ${textPrimary} text-right truncate max-w-[60%]`}
                                  >
                                    {item.value}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </div>
                </div>
              ) : activeTab === "energyanalysis" ? (
                <div
                  className="flex-1 rounded-xl border shadow-sm p-4 overflow-hidden flex flex-col"
                  style={{
                    backgroundColor: bgCard,
                    borderColor: isDark ? "#334155" : "#f3f4f6",
                  }}
                >
                  {/* Energy Analysis Header – dimensionet e njëjta si Real-time */}
                  <div className="flex items-center justify-between mb-4 flex-shrink-0">
                    <div className="flex items-center gap-2">
                      <div className="w-1 h-5 bg-purple-500 rounded"></div>
                      <h3 className={`font-semibold ${textPrimary}`}>
                        {t("energyAnalysis")}
                      </h3>
                    </div>
                    <div className="flex items-center gap-3">
                      <div
                        className={`flex rounded-lg p-1 ${isDark ? "bg-[#334155]" : "bg-gray-100"}`}
                      >
                        {["day", "week", "month", "year", "total"].map((period) => (
                          <button
                            key={period}
                            onClick={() => setAnalysisPeriod(period)}
                            className={`px-4 py-1.5 rounded-md text-sm font-medium transition ${analysisPeriod === period
                              ? isDark
                                ? "bg-[#1e293b] text-purple-400 shadow-sm"
                                : "bg-white text-purple-600 shadow-sm"
                              : isDark
                                ? "text-gray-400 hover:text-gray-300"
                                : "text-gray-500 hover:text-gray-700"
                              }`}
                          >
                            {period === "day"
                              ? t("dayLabel")
                              : period === "week"
                                ? t("week")
                                : period === "month"
                                  ? t("monthLabel")
                                  : period === "year"
                                    ? t("yearLabel")
                                    : t("total")}
                          </button>
                        ))}
                      </div>
                      {(analysisPeriod === "day" || analysisPeriod === "week" || analysisPeriod === "month" || analysisPeriod === "year") && (
                        <div
                          className={`flex items-center gap-2 border rounded-lg px-3 py-1.5 ${isDark ? "border-[#334155]" : "border-gray-200"}`}
                        >
                          <button
                            type="button"
                            onClick={() => changeAnalysisDate(-1)}
                            className={`${textSecondary} hover:opacity-80`}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                          </button>
                          <span className={`text-sm ${textPrimary}`}>{analysisDateDisplay}</span>
                          <button
                            type="button"
                            onClick={() => changeAnalysisDate(1)}
                            className={`${textSecondary} hover:opacity-80`}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-4 mb-4 flex-shrink-0">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded"
                        style={{ backgroundColor: chartColor }}
                      ></div>
                      <span className={`text-sm ${textSecondary}`}>{t("solar")}</span>
                    </div>
                  </div>
                  <div
                    className="flex-1 min-h-0 cursor-pointer"
                    onTouchStart={handleChartLongPressStart}
                    onTouchEnd={handleChartLongPressEnd}
                    onMouseDown={handleChartLongPressStart}
                    onMouseUp={handleChartLongPressEnd}
                    onMouseLeave={handleChartLongPressEnd}
                  >
                    <ResponsiveContainer width="100%" height="100%">
                      {analysisPeriod === "day" ? (
                        <AreaChart
                          data={analysisChartData}
                          margin={{ top: 24, right: 20, left: 12, bottom: 28 }}
                        >
                          <defs><linearGradient id="invAnalysisFill" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={chartColor} stopOpacity={0.35} /><stop offset="100%" stopColor={chartColor} stopOpacity={0} /></linearGradient></defs>
                          <CartesianGrid
                            strokeDasharray="3 3"
                            stroke={isDark ? "#334155" : "#f0f0f0"}
                            vertical={false}
                          />
                          <XAxis
                            dataKey="name"
                            axisLine={false}
                            tickLine={false}
                            tick={{ fill: isDark ? "#9ca3af" : "#6b7280", fontSize: 12 }}
                            interval={11}
                            dy={8}
                          />
                          <YAxis
                            axisLine={false}
                            tickLine={false}
                            width={52}
                            padding={{ top: 12, bottom: 12 }}
                            tick={{ fill: isDark ? "#9ca3af" : "#6b7280", fontSize: 12 }}
                            tickFormatter={(value) => `${Number(value).toFixed(0)} kWh`}
                          />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: isDark ? "#1e293b" : "#fff",
                              border: `1px solid ${isDark ? "#334155" : "#e5e7eb"}`,
                              borderRadius: "8px",
                              color: isDark ? "#fff" : "#000",
                            }}
                            labelFormatter={(label) => (label ? `Koha: ${label}` : label)}
                            formatter={(value) => [`${Number(value).toFixed(0)} kWh`, "Energy"]}
                          />
                          <Area type="linear" dataKey="value" fill="url(#invAnalysisFill)" stroke="none" />
                          <Line
                            type="linear"
                            dataKey="value"
                            stroke={chartColor}
                            strokeWidth={2}
                            dot={{ fill: chartColor, r: 2 }}
                            activeDot={{ r: 4 }}
                          />
                        </AreaChart>
                      ) : (
                        <BarChart
                          data={analysisChartData}
                          margin={{ top: 24, right: 20, left: 12, bottom: 28 }}
                        >
                          <CartesianGrid
                            strokeDasharray="3 3"
                            stroke={isDark ? "#334155" : "#f0f0f0"}
                            vertical={false}
                          />
                          <XAxis
                            dataKey="name"
                            axisLine={false}
                            tickLine={false}
                            tick={{ fill: isDark ? "#9ca3af" : "#6b7280", fontSize: 12, angle: -45, textAnchor: "end" }}
                            interval="preserveStartEnd"
                            dy={8}
                          />
                          <YAxis
                            axisLine={false}
                            tickLine={false}
                            width={52}
                            padding={{ top: 12, bottom: 12 }}
                            tick={{ fill: isDark ? "#9ca3af" : "#6b7280", fontSize: 12 }}
                            tickFormatter={(value) => `${Number(value).toFixed(0)} kWh`}
                          />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: isDark ? "#1e293b" : "#fff",
                              border: `1px solid ${isDark ? "#334155" : "#e5e7eb"}`,
                              borderRadius: "8px",
                              color: isDark ? "#fff" : "#000",
                            }}
                            formatter={(value) => [`${Number(value).toFixed(0)} kWh`, "Energy"]}
                          />
                          <Bar dataKey="value" fill={chartColor} radius={[5, 5, 0, 0]} />
                        </BarChart>
                      )}
                    </ResponsiveContainer>
                  </div>
                </div>
              ) : (
                <div
                  className="flex-1 rounded-xl border shadow-sm p-4 overflow-hidden flex flex-col"
                  style={{
                    backgroundColor: bgCard,
                    borderColor: isDark ? "#334155" : "#f3f4f6",
                  }}
                >
                  {/* Real-time Header – Day/Month/Year/Lifetime + Date */}
                  <div className="flex flex-col gap-3 mb-4 flex-shrink-0">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-1 h-5 bg-purple-500 rounded"></div>
                        <h3 className={`font-semibold ${textPrimary}`}>
                          {t("realtimeCurve")}
                        </h3>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 flex-wrap">
                      {["Day", "Month", "Year", "Lifetime"].map((p) => (
                        <button
                          key={p}
                          onClick={() => setWebChartPeriod(p)}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${webChartPeriod === p
                            ? "bg-purple-600 text-white"
                            : isDark ? "bg-[#334155] text-gray-400 hover:text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                            }`}
                        >
                          {p === "Day" ? t("dayLabel") : p === "Month" ? t("monthLabel") : p === "Year" ? t("yearLabel") : t("lifetime")}
                        </button>
                      ))}
                      {webChartPeriod !== "Lifetime" && (
                        <div className="flex items-center gap-2">
                          <input
                            ref={realtimeDatePickerRef}
                            type="date"
                            max={invTodayStr()}
                            value={dateStr}
                            onChange={(e) => e.target.value && setWebSelectedDate(clampInvChartDate(e.target.value, webChartPeriod))}
                            className="sr-only"
                            tabIndex={-1}
                            aria-hidden
                          />
                          <input
                            ref={realtimeMonthPickerRef}
                            type="month"
                            max={invTodayStr().slice(0, 7)}
                            value={dateStr.slice(0, 7)}
                            onChange={(e) => e.target.value && setWebSelectedDate(clampInvChartDate(`${e.target.value}-01`, webChartPeriod))}
                            className="sr-only"
                            tabIndex={-1}
                            aria-hidden
                          />
                          <button onClick={() => changeWebDate(-1)} className={`p-1 ${textSecondary} hover:opacity-80`}>◀</button>
                          {webChartPeriod === "Year" ? (
                            <select
                              value={dateStr.slice(0, 4)}
                              onChange={(e) => setWebSelectedDate(clampInvChartDate(`${e.target.value}-01-01`, "Year"))}
                              className={`text-sm min-w-[4.5rem] rounded-md border px-2 py-1 ${isDark ? "bg-[#1e293b] border-slate-600 text-white" : "bg-white border-gray-200 text-gray-900"}`}
                            >
                              {Array.from({ length: Math.max(1, new Date().getFullYear() - 2019) }, (_, i) => 2020 + i)
                                .filter((y) => y <= new Date().getFullYear())
                                .map((y) => (
                                  <option key={y} value={y}>{y}</option>
                                ))}
                            </select>
                          ) : (
                            <button
                              type="button"
                              onClick={openWebRealtimePeriodPicker}
                              className={`text-sm ${textPrimary} min-w-[100px] text-center`}
                            >
                              {realtimeGraphDateDisplay}
                            </button>
                          )}
                          <button onClick={() => changeWebDate(1)} className={`p-1 ${textSecondary} hover:opacity-80`}>▶</button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Legend */}
                  <div className="flex items-center gap-4 mb-4 flex-shrink-0">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-0.5"
                        style={{ backgroundColor: chartColor }}
                      ></div>
                      <span className={`text-sm ${textSecondary}`}>
                        {t("pvPower")}
                      </span>
                    </div>
                  </div>

                  {/* Line Chart - Long press to change color */}
                  <div
                    className="flex-1 min-h-0 cursor-pointer relative"
                    onTouchStart={handleChartLongPressStart}
                    onTouchEnd={handleChartLongPressEnd}
                    onMouseDown={handleChartLongPressStart}
                    onMouseUp={handleChartLongPressEnd}
                    onMouseLeave={handleChartLongPressEnd}
                  >
                    {chartLoading && (
                      <div className="absolute inset-0 flex items-center justify-center bg-white/80 dark:bg-[#0f172a]/80 z-10 rounded-lg">
                        <div className="flex flex-col items-center gap-2">
                          <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
                          <span className="text-sm text-gray-600 dark:text-gray-400">{t("loading")}</span>
                        </div>
                      </div>
                    )}
                    <ResponsiveContainer width="100%" height="100%">
                      {webChartPeriod === "Day" ? (
                        <AreaChart
                          data={realTimeChartData}
                          margin={{ top: 24, right: 20, left: 12, bottom: 28 }}
                        >
                          <defs><linearGradient id="invRealtimeFill" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={chartColor} stopOpacity={0.35} /><stop offset="100%" stopColor={chartColor} stopOpacity={0} /></linearGradient></defs>
                          <CartesianGrid
                            strokeDasharray="3 3"
                            stroke={isDark ? "#334155" : "#f0f0f0"}
                            vertical={false}
                          />
                          <XAxis
                            dataKey={webChartDataKey}
                            axisLine={false}
                            tickLine={false}
                            tick={{ fill: isDark ? "#9ca3af" : "#6b7280", fontSize: 12 }}
                            interval={0}
                            ticks={["00:00", "04:00", "08:00", "12:00", "16:00", "20:00", "23:00"]}
                            dy={8}
                          />
                          <YAxis
                            axisLine={false}
                            tickLine={false}
                            width={52}
                            padding={{ top: 12, bottom: 12 }}
                            tick={{ fill: isDark ? "#9ca3af" : "#6b7280", fontSize: 12 }}
                            tickFormatter={(value) => `${Number(value).toFixed(0)} kW`}
                          />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: isDark ? "#1e293b" : "#fff",
                              border: `1px solid ${isDark ? "#334155" : "#e5e7eb"}`,
                              borderRadius: "8px",
                              color: isDark ? "#fff" : "#000",
                            }}
                            labelFormatter={(label) => (label ? `Koha: ${label}` : label)}
                            formatter={(value) => [
                              typeof value === "number"
                                ? `${Number(value).toFixed(0)} kW`
                                : `${Number(value).toFixed(0)} kW`,
                              t("power"),
                            ]}
                          />
                          <Area type="linear" dataKey="value" fill="url(#invRealtimeFill)" stroke="none" />
                          <Line
                            type="linear"
                            dataKey="value"
                            stroke={chartColor}
                            strokeWidth={2}
                            dot={false}
                          />
                        </AreaChart>
                      ) : (
                        <BarChart
                          data={realTimeChartData}
                          margin={{ top: 24, right: 20, left: 12, bottom: 28 }}
                        >
                          <CartesianGrid
                            strokeDasharray="3 3"
                            stroke={isDark ? "#334155" : "#f0f0f0"}
                            vertical={false}
                          />
                          <XAxis
                            dataKey={webChartDataKey}
                            axisLine={false}
                            tickLine={false}
                            tick={{ fill: isDark ? "#9ca3af" : "#6b7280", fontSize: 12, angle: -45, textAnchor: "end" }}
                            interval="preserveStartEnd"
                            dy={8}
                          />
                          <YAxis
                            axisLine={false}
                            tickLine={false}
                            width={52}
                            padding={{ top: 12, bottom: 12 }}
                            tick={{ fill: isDark ? "#9ca3af" : "#6b7280", fontSize: 12 }}
                            tickFormatter={(value) => `${Number(value).toFixed(0)} kW`}
                          />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: isDark ? "#1e293b" : "#fff",
                              border: `1px solid ${isDark ? "#334155" : "#e5e7eb"}`,
                              borderRadius: "8px",
                              color: isDark ? "#fff" : "#000",
                            }}
                            formatter={(value) => [
                              typeof value === "number"
                                ? `${Number(value).toFixed(0)} kW`
                                : `${Number(value).toFixed(0)} kW`,
                              t("power"),
                            ]}
                          />
                          <Bar dataKey="value" fill={chartColor} radius={[5, 5, 0, 0]} />
                        </BarChart>
                      )}
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Photo Upload Modal - mobile: në mes, X */}
        {showPhotoModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div
              className="rounded-2xl w-full max-w-sm p-4"
              style={{ backgroundColor: bgCard }}
            >
              <div className="flex items-center justify-between mb-3">
                <h3
                  className={`text-base md:text-lg font-semibold ${textPrimary}`}
                >
                  Ngarko Foto për &quot;{inverter.name}&quot;
                </h3>
                <button
                  type="button"
                  onClick={() => setShowPhotoModal(false)}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400"
                  aria-label="Mbyll"
                >
                  ✕
                </button>
              </div>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handlePhotoUpload}
                accept="image/*"
                className="hidden"
              />
              <div className="flex gap-2">
                <button
                  onClick={() => setShowPhotoModal(false)}
                  className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium ${isDark ? "bg-[#334155] text-white" : "bg-gray-100 text-gray-700"}`}
                  disabled={uploading}
                >
                  Anulo
                </button>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium disabled:opacity-50"
                  disabled={uploading}
                >
                  {uploading ? "Duke ngarkuar..." : "Zgjidh Foto"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default InverterDetail;
